import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Users,
  Plus,
  DollarSign,
  Phone,
  Target,
  Home,
  Trash2,
  Edit,
  Save,
  X,
  Trophy,
  Star,
  Flame,
  Zap,
  Award,
  Music,
  Volume2,
  Play,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Crown,
  FileSpreadsheet,
  Upload,
  CheckCircle,
  AlertCircle,
  FileText,
  BarChart3,
  PieChart,
  Activity,
  PhoneOff,
  PhoneMissed,
  ThumbsDown,
  Clock,
  Brain,
  Download,
  Bell,
  TrendingDown,
  Calendar,
  Filter,
  ArrowUp,
  ArrowDown,
  Gauge,
  UserPlus,
  Key,
  Eye,
  EyeOff,
  Menu,
  ChevronLeft,
  RefreshCw,
  Search,
  Link2,
  Database,
  GraduationCap,
  BookOpen,
  MessageSquare,
  User,
  XCircle
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart as RePieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import {
  ChartsTab,
  FunnelTab,
  AgentComparisonTab,
  PredictionsTab,
  AlertsTab,
  ExportTab
} from '../components/AdvancedAnalytics';
import AIAnalysis from '../components/AIAnalysis';
import DataCenter from '../components/DataCenter';
import CRMDataTracker from '../components/CRMDataTracker';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with auth
const authAxios = axios.create();
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AVATAR_COLORS = [
  "#10b981", "#3b82f6", "#f43f5e", "#fbbf24", "#8b5cf6",
  "#ec4899", "#06b6d4", "#f97316", "#84cc16", "#6366f1"
];

const BADGE_OPTIONS = [
  { value: "daily_star", label: "Günün Yıldızı", icon: Star, color: "text-blue-400 bg-blue-500/10" },
  { value: "sales_champion", label: "Satış Şampiyonu", icon: Trophy, color: "text-amber-400 bg-amber-500/10" },
  { value: "call_master", label: "Arama Ustası", icon: Phone, color: "text-emerald-400 bg-emerald-500/10" },
  { value: "top_performer", label: "Top Performer", icon: Zap, color: "text-purple-400 bg-purple-500/10" },
  { value: "streak_fire", label: "Ateşte!", icon: Flame, color: "text-rose-400 bg-rose-500/10" },
];

// Sidebar
const Sidebar = ({ activeSection, isOpen, onClose }) => {
  const [expandedGroups, setExpandedGroups] = useState(["Genel", "Danışmanlar", "Veriler", "Motivasyon"]);
  
  const toggleGroup = (groupTitle) => {
    setExpandedGroups(prev => 
      prev.includes(groupTitle) 
        ? prev.filter(g => g !== groupTitle)
        : [...prev, groupTitle]
    );
  };

  const navGroups = [
    {
      title: "Genel",
      items: [
        { id: "crm-tracker", label: "Data Takip (CRM)", icon: Users, color: "text-blue-500" },
        { id: "data-center", label: "Data Merkezi", icon: Activity, color: "text-cyan-400" },
        { id: "ai-analysis", label: "AI Analiz", icon: Brain, color: "text-violet-400" },
      ]
    },
    {
      title: "Danışmanlar",
      items: [
        { id: "agent-reports", label: "Danışman Raporları", icon: BarChart3, color: "text-purple-400" },
        { id: "agent-users", label: "Danışman Hesapları", icon: UserPlus, color: "text-teal-400" },
        { id: "agents", label: "Çalışanlar", icon: Users, color: "text-emerald-400" },
        { id: "calls", label: "Arama Ekle", icon: Phone, color: "text-blue-400" },
      ]
    },
    {
      title: "Veriler",
      items: [
        { id: "students", label: "Öğrenciler", icon: GraduationCap, color: "text-indigo-400" },
        { id: "sales", label: "Satışlar", icon: DollarSign, color: "text-green-400" },
        { id: "kariyer-paneli", label: "KariyerPaneli CRM", icon: Database, color: "text-cyan-400" },
        { id: "excel-import", label: "Excel Yükle", icon: FileSpreadsheet, color: "text-lime-400" },
        { id: "goals", label: "Hedefler", icon: Target, color: "text-rose-400" },
      ]
    },
    {
      title: "Motivasyon",
      items: [
        { id: "slogans", label: "Sloganlar", icon: Flame, color: "text-orange-400" },
        { id: "music", label: "Kutlama Müziği", icon: Music, color: "text-pink-400" },
      ]
    },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`
          w-72 min-h-screen bg-white border-r border-slate-200 p-4 md:p-6 fixed left-0 top-0 z-50
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Close button - Mobile only */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg bg-slate-200 text-slate-500 lg:hidden"
        >
          <X size={20} />
        </button>

        {/* Logo/Brand */}
        <div className="mb-6 md:mb-8">
          <Link to="/" className="flex items-center gap-3 group" onClick={onClose}>
            <div className="p-2 rounded-xl bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
              <Home size={20} className="text-emerald-400" />
            </div>
            <span className="font-heading font-bold text-base md:text-lg text-slate-700 group-hover:text-slate-800 transition-colors">
              Dashboard'a Dön
            </span>
          </Link>
        </div>
        
        <div className="mb-4 md:mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-500 bg-clip-text text-transparent">
            Admin Panel
          </h2>
          <p className="text-slate-400 text-xs md:text-sm mt-1">Ekip yönetimi</p>
        </div>
        
        <nav className="space-y-2 max-h-[60vh] overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.title}>
              <button
                onClick={() => toggleGroup(group.title)}
                className="w-full flex items-center justify-between text-xs text-slate-500 font-semibold uppercase tracking-wider px-3 py-2 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <span>{group.title}</span>
                <ChevronDown 
                  size={14} 
                  className={`transition-transform duration-200 ${expandedGroups.includes(group.title) ? 'rotate-180' : ''}`} 
                />
              </button>
              <AnimatePresence>
                {expandedGroups.includes(group.title) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-1 mt-1">
                      {group.items.map((item) => (
                        <Link
                          key={item.id}
                          to={`/admin/${item.id}`}
                          data-testid={`nav-${item.id}`}
                          onClick={onClose}
                        >
                          <motion.div
                            whileHover={{ x: 4 }}
                            className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-2.5 rounded-xl transition-all ${
                              activeSection === item.id
                                ? "bg-blue-50 border border-blue-200"
                                : "hover:bg-slate-100"
                            }`}
                          >
                            <div className={`p-1.5 rounded-lg ${activeSection === item.id ? item.color.replace('text-', 'bg-').replace('400', '500/20') : 'bg-slate-200'}`}>
                              <item.icon size={14} className={activeSection === item.id ? item.color : 'text-slate-500'} />
                            </div>
                            <span className={`text-sm ${activeSection === item.id ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>
                              {item.label}
                            </span>
                            {activeSection === item.id && (
                              <ChevronRight size={14} className="ml-auto text-slate-500 hidden md:block" />
                            )}
                          </motion.div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>

        {/* Quick Stats - Hidden on very small screens */}
        <div className="mt-6 md:mt-8 p-3 md:p-4 rounded-xl bg-blue-50/50 border border-blue-100 hidden sm:block">
          <p className="text-slate-500 text-xs md:text-sm mb-2">Hızlı Bakış</p>
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-emerald-400" />
            <span className="text-slate-700 text-xs md:text-sm">Sistem aktif</span>
          </div>
        </div>
      </div>
    </>
  );
};

// Agent Users Management Section
const AgentUsersSection = () => {
  const [agentUsers, setAgentUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({ agent_id: "", username: "", password: "" });
  const [showPassword, setShowPassword] = useState({});
  const [editingPassword, setEditingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const fetchAgentUsers = async () => {
    try {
      const res = await authAxios.get(`${API}/agent-users`);
      setAgentUsers(res.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Danışman kullanıcıları yüklenemedi");
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await authAxios.get(`${API}/agents`);
      setAgents(res.data.filter(a => a.is_active !== false));
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAgentUsers(), fetchAgents()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const createAgentUser = async () => {
    if (!newUser.agent_id || !newUser.username || !newUser.password) {
      toast.error("Tüm alanları doldurun");
      return;
    }

    try {
      await authAxios.post(`${API}/agent-users`, newUser);
      toast.success("Danışman hesabı oluşturuldu");
      setShowCreateDialog(false);
      setNewUser({ agent_id: "", username: "", password: "" });
      fetchAgentUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hesap oluşturulamadı");
    }
  };

  const updatePassword = async (userId) => {
    if (!newPassword) {
      toast.error("Yeni şifre girin");
      return;
    }

    try {
      await authAxios.put(`${API}/agent-users/${userId}`, { password: newPassword });
      toast.success("Şifre güncellendi");
      setEditingPassword(null);
      setNewPassword("");
    } catch (error) {
      toast.error("Şifre güncellenemedi");
    }
  };

  const toggleUserActive = async (user) => {
    try {
      await authAxios.put(`${API}/agent-users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? "Hesap devre dışı bırakıldı" : "Hesap aktif edildi");
      fetchAgentUsers();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Bu hesabı silmek istediğinizden emin misiniz?")) return;

    try {
      await authAxios.delete(`${API}/agent-users/${userId}`);
      toast.success("Hesap silindi");
      fetchAgentUsers();
    } catch (error) {
      toast.error("Hesap silinemedi");
    }
  };

  // Get agents without user accounts
  const availableAgents = agents.filter(
    agent => !agentUsers.some(u => u.agent_id === agent.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          icon={UserPlus}
          title="Danışman Hesapları"
          description="Danışmanlar için giriş hesapları oluşturun ve yönetin"
          color="bg-cyan-500/10 text-cyan-400"
        />
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-cyan-600 hover:bg-cyan-500"
          disabled={availableAgents.length === 0}
        >
          <Plus size={18} className="mr-2" />
          Yeni Hesap
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <p className="text-slate-500 text-sm">Toplam Hesap</p>
            <p className="text-2xl font-bold text-slate-800">{agentUsers.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <p className="text-slate-500 text-sm">Aktif Hesap</p>
            <p className="text-2xl font-bold text-emerald-400">
              {agentUsers.filter(u => u.is_active !== false).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <p className="text-slate-500 text-sm">Hesapsız Danışman</p>
            <p className="text-2xl font-bold text-amber-400">{availableAgents.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle>Danışman Hesapları</CardTitle>
          <CardDescription>Sisteme giriş yapabilen danışmanlar</CardDescription>
        </CardHeader>
        <CardContent>
          {agentUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <UserPlus size={48} className="mx-auto mb-4 opacity-50" />
              <p>Henüz danışman hesabı yok</p>
              <p className="text-sm mt-1">Yukarıdaki "Yeni Hesap" butonuna tıklayın</p>
            </div>
          ) : (
            <div className="space-y-3">
              {agentUsers.map((user) => (
                <div
                  key={user.id}
                  className={`p-4 rounded-xl border transition-all ${
                    user.is_active === false
                      ? "bg-slate-50 border-slate-300 opacity-60"
                      : "bg-slate-100 border-slate-300 hover:border-blue-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-cyan-500/10">
                        <Users size={20} className="text-cyan-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{user.agent_name}</p>
                        <p className="text-sm text-slate-500">@{user.username}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingPassword === user.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="password"
                            placeholder="Yeni şifre"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-32 h-8 text-sm bg-white border-slate-300"
                          />
                          <Button size="sm" onClick={() => updatePassword(user.id)}>
                            <Save size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingPassword(null)}>
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-300"
                            onClick={() => setEditingPassword(user.id)}
                          >
                            <Key size={14} className="mr-1" />
                            Şifre
                          </Button>
                          <Button
                            size="sm"
                            variant={user.is_active === false ? "default" : "outline"}
                            className={user.is_active === false ? "bg-emerald-600" : "border-slate-300"}
                            onClick={() => toggleUserActive(user)}
                          >
                            {user.is_active === false ? "Aktif Et" : "Devre Dışı"}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => deleteUser(user.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md border border-slate-200 shadow-lg"
          >
            <h3 className="text-xl font-bold text-slate-800 mb-4">Yeni Danışman Hesabı</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-500">Danışman</Label>
                <select
                  value={newUser.agent_id}
                  onChange={(e) => setNewUser({ ...newUser, agent_id: e.target.value })}
                  className="w-full mt-1 p-2 rounded-lg bg-slate-200 border border-slate-300 text-slate-800"
                >
                  <option value="">Danışman seçin...</option>
                  {availableAgents.map((agent) => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-slate-500">Kullanıcı Adı</Label>
                <Input
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                  placeholder="ornek: seher.yildiz"
                  className="mt-1 bg-white border-slate-300"
                />
              </div>
              
              <div>
                <Label className="text-slate-500">Şifre</Label>
                <div className="relative">
                  <Input
                    type={showPassword.new ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="En az 6 karakter"
                    className="mt-1 bg-white border-slate-300 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                  >
                    {showPassword.new ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1 border-slate-300"
                onClick={() => setShowCreateDialog(false)}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-cyan-600 hover:bg-cyan-500"
                onClick={createAgentUser}
              >
                Oluştur
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Agent Reports Section - Danışman Raporları ve Analizleri
const AgentReportsSection = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("comparison"); // comparison, detail

  const fetchAgents = async () => {
    try {
      const res = await authAxios.get(`${API}/agents`);
      setAgents(res.data.filter(a => a.is_active !== false));
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchComparison = async () => {
    try {
      const res = await authAxios.get(`${API}/admin/comparison`);
      setComparison(res.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Karşılaştırma verileri yüklenemedi");
    }
  };

  const fetchAgentDetails = async (agentId) => {
    try {
      const res = await authAxios.get(`${API}/admin/agent-details/${agentId}`);
      setAgentDetails(res.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Danışman detayları yüklenemedi");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAgents(), fetchComparison()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSelectAgent = async (agent) => {
    setSelectedAgent(agent);
    setViewMode("detail");
    await fetchAgentDetails(agent.agent_id || agent.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          icon={BarChart3}
          title="Danışman Raporları"
          description="Danışman performanslarını karşılaştırın ve analiz edin"
          color="bg-purple-500/10 text-purple-400"
        />
        <div className="flex gap-2">
          <Button
            variant={viewMode === "comparison" ? "default" : "outline"}
            onClick={() => setViewMode("comparison")}
            className={viewMode === "comparison" ? "bg-purple-600" : "border-slate-300"}
          >
            <BarChart3 size={16} className="mr-2" />
            Karşılaştırma
          </Button>
          <Button
            variant={viewMode === "detail" ? "default" : "outline"}
            onClick={() => setViewMode("detail")}
            className={viewMode === "detail" ? "bg-purple-600" : "border-slate-300"}
            disabled={!selectedAgent}
          >
            <Users size={16} className="mr-2" />
            Detay
          </Button>
        </div>
      </div>

      {viewMode === "comparison" ? (
        <>
          {/* Comparison Summary */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <p className="text-slate-500 text-sm">Toplam Danışman</p>
                <p className="text-2xl font-bold text-slate-800">{comparison.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <p className="text-slate-500 text-sm">Bu Ay Toplam Satış</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {comparison.reduce((sum, a) => sum + a.sales_count, 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <p className="text-slate-500 text-sm">Bu Ay Toplam Ciro</p>
                <p className="text-2xl font-bold text-amber-400">
                  ₺{comparison.reduce((sum, a) => sum + a.revenue, 0).toLocaleString('tr-TR')}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <p className="text-slate-500 text-sm">Ortalama Dönüşüm</p>
                <p className="text-2xl font-bold text-blue-400">
                  %{(comparison.reduce((sum, a) => sum + a.conversion_rate, 0) / comparison.length || 0).toFixed(1)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Comparison Table */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle>Aylık Performans Karşılaştırması</CardTitle>
              <CardDescription>Ciro sıralamasına göre</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {comparison.map((agent, index) => (
                  <motion.div
                    key={agent.agent_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleSelectAgent(agent)}
                    className="p-4 rounded-xl bg-slate-100 border border-slate-300 hover:border-purple-500/50 cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-amber-500 text-black' :
                        index === 1 ? 'bg-slate-400 text-black' :
                        index === 2 ? 'bg-amber-700 text-slate-800' :
                        'bg-slate-300 text-slate-700'
                      }`}>
                        {index + 1}
                      </div>

                      {/* Avatar & Name */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-slate-800 font-bold"
                          style={{ backgroundColor: agent.avatar_color }}
                        >
                          {agent.name?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{agent.name}</p>
                          <p className="text-sm text-slate-500">{agent.calls_count} arama</p>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-8 text-center">
                        <div>
                          <p className="text-lg font-bold text-emerald-400">{agent.sales_count}</p>
                          <p className="text-xs text-slate-500">Satış</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-amber-400">₺{agent.revenue.toLocaleString('tr-TR')}</p>
                          <p className="text-xs text-slate-500">Ciro</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-blue-400">%{agent.conversion_rate.toFixed(1)}</p>
                          <p className="text-xs text-slate-500">Dönüşüm</p>
                        </div>
                      </div>

                      <ChevronRight size={20} className="text-slate-400" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Revenue Chart */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle>Ciro Karşılaştırması</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-end gap-2">
                {comparison.slice(0, 10).map((agent, i) => {
                  const maxRevenue = Math.max(...comparison.map(a => a.revenue), 1);
                  const height = (agent.revenue / maxRevenue) * 100;
                  return (
                    <div key={agent.agent_id} className="flex-1 flex flex-col items-center gap-2">
                      <div
                        className="w-full rounded-t-lg transition-all"
                        style={{
                          height: `${Math.max(height, 5)}%`,
                          backgroundColor: agent.avatar_color || '#10b981'
                        }}
                      />
                      <span className="text-xs text-slate-500 truncate w-full text-center">
                        {agent.name?.split(' ')[0]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        /* Detail View */
        agentDetails ? (
          <div className="space-y-6">
            {/* Agent Header */}
            <Card className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border-purple-500/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold text-slate-800"
                    style={{ backgroundColor: agentDetails.agent?.avatar_color }}
                  >
                    {agentDetails.agent?.name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-slate-800">{agentDetails.agent?.name}</h2>
                    {agentDetails.agent_user && (
                      <p className="text-slate-500">@{agentDetails.agent_user.username}</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    className="border-slate-300"
                    onClick={() => setViewMode("comparison")}
                  >
                    Listeye Dön
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Summary */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <p className="text-slate-500 text-sm">Bu Ay Satış</p>
                  <p className="text-2xl font-bold text-emerald-400">{agentDetails.month_summary?.total_sales}</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <p className="text-slate-500 text-sm">Bu Ay Ciro</p>
                  <p className="text-2xl font-bold text-amber-400">
                    ₺{(agentDetails.month_summary?.total_revenue || 0).toLocaleString('tr-TR')}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <p className="text-slate-500 text-sm">Bu Ay Arama</p>
                  <p className="text-2xl font-bold text-blue-400">{agentDetails.month_summary?.total_calls}</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <p className="text-slate-500 text-sm">Günlük Ort. Arama</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {(agentDetails.month_summary?.avg_daily_calls || 0).toFixed(1)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Status Distribution */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle>Arama Durum Dağılımı</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(agentDetails.status_distribution || {}).map(([status, count]) => (
                    <div key={status} className="p-3 rounded-lg bg-slate-100 border border-slate-300">
                      <p className="text-sm text-slate-500 truncate">{status}</p>
                      <p className="text-xl font-bold text-slate-800">{count}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Daily Performance Chart */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle>Günlük Performans (Son 30 Gün)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48 flex items-end gap-1">
                  {(agentDetails.daily_performance || []).map((day, i) => {
                    const maxSales = Math.max(...(agentDetails.daily_performance || []).map(d => d.sales), 1);
                    const height = (day.sales / maxSales) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t transition-all hover:from-emerald-500"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${day.date}: ${day.sales} satış, ₺${day.revenue}`}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-slate-400">30 gün önce</span>
                  <span className="text-xs text-slate-400">Bugün</span>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-slate-500">
            <p>Detay görmek için bir danışman seçin</p>
          </div>
        )
      )}
    </div>
  );
};

// Excel Import Section - Birleşik Excel Yükleme
const ExcelImportSection = () => {
  const [uploadType, setUploadType] = useState("sales"); // sales, calls
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  // Parse Sales Excel (TSV format, UTF-16)
  const parseSalesExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error('Dosyada veri bulunamadı'));
            return;
          }

          const headers = lines[0].split('\t').map(h => h.trim());
          const data = [];

          const colMap = {
            adSoyad: headers.findIndex(h => h.toLowerCase().includes('adsoyad') || h === 'AdSoyad'),
            kayKullAdSoyad: headers.findIndex(h => h.toLowerCase().includes('kaykulladsoyad') || h === 'KayKullAdSoyad'),
            toplamBorc: headers.findIndex(h => h.toLowerCase().includes('toplamborc') || h === 'ToplamBorc'),
            toplamOdenen: headers.findIndex(h => h.toLowerCase().includes('toplamoden') || h === 'ToplamOdenen'),
            kayTar: headers.findIndex(h => h.toLowerCase().includes('kaytar') || h === 'KayTar'),
          };

          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split('\t').map(c => c.trim());
            const agentName = cols[colMap.kayKullAdSoyad] || '';
            const amount = parseFloat(cols[colMap.toplamBorc]?.replace(',', '.')) || 0;
            const collected = parseFloat(cols[colMap.toplamOdenen]?.replace(',', '.')) || 0;
            const customerName = cols[colMap.adSoyad] || '';
            const dateStr = cols[colMap.kayTar] || '';

            if (!agentName || agentName === 'Referans' || agentName === 'ınsta' || amount <= 0) continue;

            let timestamp = '';
            try {
              const [datePart, timePart] = dateStr.split(' ');
              const [day, month, year] = datePart.split('.');
              timestamp = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart || '12:00:00'}`;
            } catch {
              timestamp = new Date().toISOString();
            }

            data.push({
              agent_name: agentName,
              amount: amount,
              collected_amount: collected,
              customer_name: customerName,
              timestamp: timestamp,
              description: ''
            });
          }

          resolve(data);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file, 'UTF-16');
    });
  };

  // Parse Calls Excel (TSV format, UTF-16)
  const parseCallsExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error('Dosyada veri bulunamadı'));
            return;
          }

          const headers = lines[0].split('\t').map(h => h.trim());
          const data = [];

          const colMap = {
            dataDurum: headers.findIndex(h => h.includes('DataDurumAdi') || h === 'DataDurumAdi'),
            danisman: headers.findIndex(h => h.includes('DanismanAdSoyad') || h === 'DanismanAdSoyad'),
            aciklama: headers.findIndex(h => h.includes('Aciklama') || h === 'Aciklama'),
            kayTar: headers.findIndex(h => h.includes('KayTar') || h === 'KayTar'),
            adSoyad: headers.findIndex(h => h === 'AdSoyad'),
            dersAdi: headers.findIndex(h => h.includes('DersAdi') || h === 'DersAdi'),
            egitimDurumu: headers.findIndex(h => h.includes('EgitimDurumuText') || h === 'EgitimDurumuText'),
            telefon: headers.findIndex(h => h.includes('Telefon') || h === 'Telefon'),
            tarih: headers.findIndex(h => h === 'Tarih'),
            sonDataDurum: headers.findIndex(h => h.includes('SonDataDurumAdi') || h === 'SonDataDurumAdi'),
            reklamTuru: headers.findIndex(h => h.includes('ReklamTuruAdi') || h === 'ReklamTuruAdi'),
            reklamTuruText: headers.findIndex(h => h.includes('ReklamTuruText') || h === 'ReklamTuruText'),
            dataAciklama: headers.findIndex(h => h.includes('DataAciklama') || h === 'DataAciklama'),
          };

          const cleanValue = (val) => (val === 'nan' || val === 'NaN' ? '' : val);

          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split('\t').map(c => c.trim());
            const agentName = cols[colMap.danisman] || '';
            const status = cols[colMap.dataDurum] || '';
            const note = cols[colMap.aciklama] || '';
            const customerName = cols[colMap.adSoyad] || '';
            const dateStr = cols[colMap.kayTar] || '';

            const dersAdi = colMap.dersAdi >= 0 ? cols[colMap.dersAdi] || '' : '';
            const egitimDurumu = colMap.egitimDurumu >= 0 ? cols[colMap.egitimDurumu] || '' : '';
            const telefon = colMap.telefon >= 0 ? cols[colMap.telefon] || '' : '';
            const tarih = colMap.tarih >= 0 ? cols[colMap.tarih] || '' : '';
            const sonDataDurum = colMap.sonDataDurum >= 0 ? cols[colMap.sonDataDurum] || '' : '';
            const reklamTuru = colMap.reklamTuru >= 0 ? cols[colMap.reklamTuru] || '' : '';
            const reklamTuruText = colMap.reklamTuruText >= 0 ? cols[colMap.reklamTuruText] || '' : '';
            const dataAciklama = colMap.dataAciklama >= 0 ? cols[colMap.dataAciklama] || '' : '';

            if (!agentName || agentName === 'nan') continue;

            let timestamp = '';
            try {
              const [datePart, timePart] = dateStr.split(' ');
              const [day, month, year] = datePart.split('.');
              timestamp = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart || '12:00:00'}`;
            } catch {
              timestamp = new Date().toISOString();
            }

            data.push({
              agent_name: agentName,
              status: status,
              note: cleanValue(note),
              customer_name: cleanValue(customerName),
              timestamp: timestamp,
              ders_adi: cleanValue(dersAdi),
              egitim_durumu: cleanValue(egitimDurumu),
              telefon: cleanValue(telefon),
              tarih: cleanValue(tarih),
              son_data_durum: cleanValue(sonDataDurum),
              reklam_turu: cleanValue(reklamTuru),
              reklam_turu_text: cleanValue(reklamTuruText),
              data_aciklama: cleanValue(dataAciklama)
            });
          }

          resolve(data);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file, 'UTF-16');
    });
  };

  // Handle file selection and parsing
  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setUploading(true);
    setResult(null);
    setPreviewData(null);

    try {
      const parseFunction = uploadType === "sales" ? parseSalesExcel : parseCallsExcel;
      const data = await parseFunction(selectedFile);
      setPreviewData(data);
      toast.success(`${data.length} ${uploadType === "sales" ? "satış" : "arama"} verisi bulundu`);
    } catch (error) {
      toast.error(`Dosya okunamadı: ${error.message}`);
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  // Handle upload to server
  const handleUpload = async () => {
    if (!previewData || previewData.length === 0) {
      toast.error("Önce dosya seçin ve verileri önizleyin");
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const endpoint = uploadType === "sales" ? "/sales/bulk-import" : "/calls/bulk-import";
      
      // Upload in batches for large datasets
      const batchSize = 1000;
      let totalImported = 0;
      let allErrors = [];
      
      for (let i = 0; i < previewData.length; i += batchSize) {
        const batch = previewData.slice(i, i + batchSize);
        const res = await authAxios.post(`${API}${endpoint}`, batch);
        totalImported += res.data.imported || 0;
        if (res.data.errors) allErrors.push(...res.data.errors);
      }
      
      setResult({ success: true, data: { imported: totalImported, errors: allErrors, total: previewData.length } });
      toast.success(`${totalImported} ${uploadType === "sales" ? "satış" : "arama"} verisi yüklendi!`);
      setFile(null);
      setPreviewData(null);
    } catch (error) {
      setResult({ success: false, error: error.response?.data?.detail || "Yükleme başarısız" });
      toast.error("Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  // Reset when upload type changes
  const handleTypeChange = (type) => {
    setUploadType(type);
    setFile(null);
    setPreviewData(null);
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileSpreadsheet}
        title="Excel Yükle"
        description="Satış veya arama verilerini Excel dosyasından içe aktarın"
        color="bg-green-500/10 text-green-400"
      />

      {/* Upload Type Selection */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle>Veri Türü Seçin</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleTypeChange("sales")}
              className={`p-6 rounded-xl border-2 transition-all ${
                uploadType === "sales"
                  ? "border-emerald-500 bg-emerald-500/10"
                  : "border-slate-300 bg-slate-100 hover:border-blue-400"
              }`}
            >
              <DollarSign size={32} className={`mx-auto mb-3 ${uploadType === "sales" ? "text-emerald-400" : "text-slate-500"}`} />
              <p className={`font-medium ${uploadType === "sales" ? "text-slate-800" : "text-slate-500"}`}>Satış Verileri</p>
              <p className="text-sm text-slate-500 mt-1">Satış Excel dosyası yükle</p>
            </button>
            <button
              onClick={() => handleTypeChange("calls")}
              className={`p-6 rounded-xl border-2 transition-all ${
                uploadType === "calls"
                  ? "border-blue-500 bg-blue-500/10"
                  : "border-slate-300 bg-slate-100 hover:border-blue-400"
              }`}
            >
              <Phone size={32} className={`mx-auto mb-3 ${uploadType === "calls" ? "text-blue-400" : "text-slate-500"}`} />
              <p className={`font-medium ${uploadType === "calls" ? "text-slate-800" : "text-slate-500"}`}>Arama Verileri</p>
              <p className="text-sm text-slate-500 mt-1">Arama Excel dosyası yükle</p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle>{uploadType === "sales" ? "Satış" : "Arama"} Excel Dosyası</CardTitle>
          <CardDescription>
            {uploadType === "sales" 
              ? "Satış verilerini içeren Excel dosyasını seçin"
              : "Arama verilerini içeren Excel dosyasını seçin"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
              className="hidden"
              id="excel-upload"
            />
            <label htmlFor="excel-upload" className="cursor-pointer">
              <Upload size={48} className="mx-auto text-slate-500 mb-4" />
              {file ? (
                <div>
                  <p className="text-slate-800 font-medium">{file.name}</p>
                  <p className="text-sm text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                  {previewData && (
                    <p className="text-sm text-emerald-400 mt-2">{previewData.length} kayıt bulundu</p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-slate-500">Dosya seçmek için tıklayın</p>
                  <p className="text-sm text-slate-400 mt-1">.xlsx, .xls veya .csv formatında</p>
                </div>
              )}
            </label>
          </div>

          {/* Preview Summary */}
          {previewData && previewData.length > 0 && (
            <div className="p-4 rounded-xl bg-slate-100 border border-slate-300">
              <h4 className="font-semibold text-slate-800 mb-3">Veri Önizleme</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Toplam Kayıt:</span>
                  <span className="text-slate-800 ml-2 font-medium">{previewData.length}</span>
                </div>
                <div>
                  <span className="text-slate-500">Benzersiz {uploadType === "sales" ? "Danışman" : "Danışman"}:</span>
                  <span className="text-slate-800 ml-2 font-medium">
                    {new Set(previewData.map(d => d.agent_name)).size}
                  </span>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={handleUpload}
            disabled={!previewData || previewData.length === 0 || uploading}
            className={`w-full h-12 ${uploadType === "sales" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-blue-600 hover:bg-blue-500"}`}
          >
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Yükleniyor...
              </>
            ) : (
              <>
                <Upload size={18} className="mr-2" />
                {uploadType === "sales" ? "Satış Verilerini" : "Arama Verilerini"} Yükle
              </>
            )}
          </Button>

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-xl ${result.success ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
              {result.success ? (
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-emerald-400" />
                  <div>
                    <p className="font-medium text-emerald-400">Yükleme Başarılı!</p>
                    <p className="text-sm text-slate-500">
                      {result.data?.imported} kayıt eklendi
                      {result.data?.errors?.length > 0 && `, ${result.data.errors.length} hata`}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <AlertCircle className="text-red-400" />
                  <div>
                    <p className="font-medium text-red-400">Yükleme Başarısız</p>
                    <p className="text-sm text-slate-500">{result.error}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Page Header Component
const PageHeader = ({ icon: Icon, title, description, color }) => (
  <div className="mb-8">
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon size={28} className="text-current" />
      </div>
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-800">{title}</h1>
        <p className="text-slate-500 mt-1">{description}</p>
      </div>
    </div>
  </div>
);

// Agents Section
const AgentsSection = () => {
  const [agents, setAgents] = useState([]);
  const [newAgent, setNewAgent] = useState({ name: "", avatar_color: "#10b981" });
  const [editingAgent, setEditingAgent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchAgents = async () => {
    try {
      const res = await authAxios.get(`${API}/agents`);
      setAgents(res.data);
    } catch (error) {
      toast.error("Çalışanlar yüklenemedi");
    }
  };

  useEffect(() => { fetchAgents(); }, []);

  const handleCreateAgent = async () => {
    if (!newAgent.name.trim()) {
      toast.error("İsim boş olamaz");
      return;
    }
    try {
      await authAxios.post(`${API}/agents`, newAgent);
      toast.success("Çalışan eklendi! 🎉");
      setNewAgent({ name: "", avatar_color: "#10b981" });
      setIsDialogOpen(false);
      fetchAgents();
    } catch (error) {
      toast.error("Çalışan eklenemedi");
    }
  };

  const handleUpdateAgent = async () => {
    if (!editingAgent) return;
    try {
      await authAxios.put(`${API}/agents/${editingAgent.id}`, {
        name: editingAgent.name,
        avatar_color: editingAgent.avatar_color
      });
      toast.success("Güncellendi!");
      setEditingAgent(null);
      fetchAgents();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await authAxios.delete(`${API}/agents/${agentId}`);
      toast.success("Çalışan silindi");
      fetchAgents();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  return (
    <div data-testid="agents-section">
      <PageHeader 
        icon={Users} 
        title="Çalışan Yönetimi" 
        description="Ekip üyelerini ekle, düzenle veya kaldır"
        color="bg-emerald-500/10 text-emerald-400"
      />

      <div className="flex justify-end mb-6">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-emerald-600 hover:bg-emerald-500" data-testid="add-agent-btn">
              <Plus size={18} />
              Yeni Çalışan Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-50 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-xl">Yeni Çalışan</DialogTitle>
              <DialogDescription>Ekibe yeni bir üye ekleyin</DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">İsim Soyisim</Label>
                <Input
                  id="name"
                  placeholder="Örn: Ahmet Yılmaz"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                  className="bg-white border-slate-300"
                  data-testid="agent-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Avatar Rengi</Label>
                <div className="flex flex-wrap gap-3">
                  {AVATAR_COLORS.map((color) => (
                    <motion.button
                      key={color}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-10 h-10 rounded-full transition-all ${
                        newAgent.avatar_color === color ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-white" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewAgent({ ...newAgent, avatar_color: color })}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" className="border-slate-300">İptal</Button>
              </DialogClose>
              <Button onClick={handleCreateAgent} className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-agent-btn">
                Ekle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <AnimatePresence>
          {agents.map((agent, i) => (
            <motion.div
              key={agent.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className="bg-white border-slate-200 hover:border-slate-300 transition-colors">
                <CardContent className="pt-6">
                  {editingAgent?.id === agent.id ? (
                    <div className="space-y-4">
                      <Input
                        value={editingAgent.name}
                        onChange={(e) => setEditingAgent({ ...editingAgent, name: e.target.value })}
                        className="bg-white border-slate-300"
                      />
                      <div className="flex flex-wrap gap-2">
                        {AVATAR_COLORS.map((color) => (
                          <button
                            key={color}
                            className={`w-7 h-7 rounded-full transition-all ${
                              editingAgent.avatar_color === color ? "ring-2 ring-white" : ""
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => setEditingAgent({ ...editingAgent, avatar_color: color })}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleUpdateAgent} className="bg-emerald-600 hover:bg-emerald-500">
                          <Save size={14} className="mr-1" /> Kaydet
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingAgent(null)} className="border-slate-300">
                          <X size={14} />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center font-heading font-bold text-xl shadow-lg"
                        style={{ backgroundColor: agent.avatar_color }}
                      >
                        {agent.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-800 text-lg truncate">{agent.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <TrendingUp size={12} className="text-emerald-400" />
                            {agent.total_sales} satış
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone size={12} className="text-blue-400" />
                            {agent.total_calls} arama
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditingAgent(agent)} className="hover:bg-slate-200">
                          <Edit size={16} className="text-slate-500" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteAgent(agent.id)} className="hover:bg-red-500/10">
                          <Trash2 size={16} className="text-red-400" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {agents.length === 0 && (
        <div className="text-center py-16">
          <Users size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Henüz çalışan eklenmemiş</p>
          <p className="text-slate-400 text-sm">Yukarıdaki butona tıklayarak ekleyin</p>
        </div>
      )}
    </div>
  );
};

// KariyerPaneli CRM Entegrasyonu
const KariyerPaneliSection = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [previewSales, setPreviewSales] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '.'),
    end: new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '.')
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Silinen kayıtlar state
  const [deletedStats, setDeletedStats] = useState(null);
  const [deletedSales, setDeletedSales] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedPage, setDeletedPage] = useState(1);
  const [deletedTotalPages, setDeletedTotalPages] = useState(1);

  const fetchStats = async () => {
    try {
      const response = await authAxios.get(`${API}/kariyer-paneli/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Stats yüklenirken hata:", error);
    }
  };
  
  const fetchDeletedStats = async () => {
    try {
      const response = await authAxios.get(`${API}/kariyer-paneli/deleted-stats`);
      setDeletedStats(response.data);
    } catch (error) {
      console.error("Silinen istatistikler yüklenirken hata:", error);
    }
  };
  
  const fetchDeletedSales = async (page = 1) => {
    try {
      const response = await authAxios.get(`${API}/kariyer-paneli/deleted-sales?page=${page}&limit=20`);
      setDeletedSales(response.data.sales || []);
      setDeletedTotalPages(response.data.total_pages || 1);
      setDeletedPage(page);
    } catch (error) {
      console.error("Silinen satışlar yüklenirken hata:", error);
    }
  };

  const fetchSyncedSales = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (searchTerm) params.append("agent_name", searchTerm);
      
      const response = await authAxios.get(`${API}/kariyer-paneli/synced-sales?${params}`);
      setSales(response.data.sales || []);
      setTotalPages(response.data.total_pages || 1);
      setCurrentPage(page);
    } catch (error) {
      toast.error("Satışlar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const previewFromCRM = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get(
        `${API}/kariyer-paneli/sales?start_date=${dateRange.start}&end_date=${dateRange.end}`
      );
      setPreviewSales(response.data.sales || []);
      setShowPreview(true);
      toast.success(`${response.data.count} satış bulundu`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "CRM'den veri çekilemedi");
    } finally {
      setLoading(false);
    }
  };

  const syncFromCRM = async () => {
    try {
      setSyncing(true);
      const response = await authAxios.post(`${API}/kariyer-paneli/sync`, {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      toast.success(response.data.message);
      setShowPreview(false);
      fetchStats();
      fetchSyncedSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Senkronizasyon başarısız");
    } finally {
      setSyncing(false);
    }
  };

  const [fullSyncing, setFullSyncing] = useState(false);
  const [fullSyncResult, setFullSyncResult] = useState(null);

  const fullSyncFromCRM = async () => {
    if (!window.confirm(`${dateRange.start} - ${dateRange.end} tarihleri arasındaki veriler TAM senkronize edilecek.\n\nBu işlem:\n• CRM'de silinen kayıtları sistemden de siler\n• Yeni kayıtları ekler\n• Agent istatistiklerini günceller\n\nDevam etmek istiyor musunuz?`)) {
      return;
    }
    
    try {
      setFullSyncing(true);
      setFullSyncResult(null);
      const response = await authAxios.post(`${API}/kariyer-paneli/full-sync`, {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      setFullSyncResult(response.data);
      
      if (response.data.deleted_count > 0 || response.data.new_count > 0) {
        toast.success(`Tam senkronizasyon: ${response.data.deleted_count} silindi, ${response.data.new_count} eklendi`);
      } else {
        toast.info("Veriler zaten güncel, değişiklik yok");
      }
      
      fetchStats();
      fetchSyncedSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Tam senkronizasyon başarısız");
    } finally {
      setFullSyncing(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSyncedSales();
    fetchDeletedStats();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      if (dateStr.includes("T")) {
        return new Date(dateStr).toLocaleDateString('tr-TR');
      }
      return dateStr.split(" ")[0];
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="kariyer-paneli-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Database className="text-cyan-400" />
            KariyerPaneli CRM
          </h2>
          <p className="text-slate-500 mt-1">Harici CRM sisteminden satışları senkronize edin</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <Database className="text-cyan-400" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Toplam Kayıt</p>
                  <p className="text-slate-800 text-xl font-bold">{stats.total_synced?.toLocaleString('tr-TR')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <DollarSign className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Toplam Tutar</p>
                  <p className="text-slate-800 text-xl font-bold">{formatCurrency(stats.total_amount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <Users className="text-purple-400" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">En Başarılı</p>
                  <p className="text-slate-800 text-lg font-semibold truncate">
                    {stats.top_agents?.[0]?.agent_name || "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <Clock className="text-amber-400" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Son Senkronizasyon</p>
                  <p className="text-slate-800 text-sm">
                    {stats.last_sync ? formatDate(stats.last_sync) : "Henüz yapılmadı"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sync Controls */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
            <RefreshCw className="text-cyan-400" size={20} />
            CRM Senkronizasyonu
          </CardTitle>
          <CardDescription>KariyerPaneli'den satış verilerini çekin</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-500">Başlangıç Tarihi</Label>
              <Input
                type="text"
                placeholder="01.12.2025"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="bg-white border-slate-300 text-slate-800"
              />
              <p className="text-xs text-slate-500 mt-1">Format: gg.aa.yyyy</p>
            </div>
            <div>
              <Label className="text-slate-500">Bitiş Tarihi</Label>
              <Input
                type="text"
                placeholder="31.12.2025"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="bg-white border-slate-300 text-slate-800"
              />
              <p className="text-xs text-slate-500 mt-1">Format: gg.aa.yyyy</p>
            </div>
            <div className="flex items-end gap-2">
              <Button 
                onClick={previewFromCRM} 
                disabled={loading}
                variant="outline"
                className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-200"
              >
                {loading ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Eye className="mr-2" size={16} />}
                Önizle
              </Button>
              <Button 
                onClick={syncFromCRM} 
                disabled={syncing || loading}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
              >
                {syncing ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Download className="mr-2" size={16} />}
                Yeni Ekle
              </Button>
              <Button 
                onClick={fullSyncFromCRM} 
                disabled={fullSyncing || loading}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {fullSyncing ? <RefreshCw className="animate-spin mr-2" size={16} /> : <RefreshCw className="mr-2" size={16} />}
                Tam Senkronize
              </Button>
            </div>
          </div>
          
          {/* Full Sync Result */}
          {fullSyncResult && (
            <div className="mt-4 p-4 bg-slate-100 rounded-lg border border-slate-300">
              <h4 className="text-slate-800 font-semibold mb-2">Tam Senkronizasyon Sonucu</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">CRM'deki Toplam</p>
                  <p className="text-slate-800 font-bold">{fullSyncResult.crm_total}</p>
                </div>
                <div>
                  <p className="text-slate-500">Silinen Kayıt</p>
                  <p className={`font-bold ${fullSyncResult.deleted_count > 0 ? 'text-red-400' : 'text-slate-500'}`}>
                    {fullSyncResult.deleted_count} ({formatCurrency(fullSyncResult.deleted_amount)})
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Eklenen Kayıt</p>
                  <p className={`font-bold ${fullSyncResult.new_count > 0 ? 'text-green-400' : 'text-slate-500'}`}>
                    {fullSyncResult.new_count} ({formatCurrency(fullSyncResult.new_amount)})
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Sonuç</p>
                  <p className="text-cyan-400 font-bold">{fullSyncResult.final_count} kayıt</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      {showPreview && previewSales.length > 0 && (
        <Card className="bg-white border-cyan-500/30">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg text-slate-800">Önizleme: {previewSales.length} Satış</CardTitle>
              <CardDescription>CRM'den çekilen veriler</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
              <X size={16} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-500 border-b border-slate-200 sticky top-0 bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Müşteri</th>
                    <th className="text-left p-2">Danışman</th>
                    <th className="text-left p-2">Ders</th>
                    <th className="text-right p-2">Tutar</th>
                    <th className="text-left p-2">Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {previewSales.slice(0, 50).map((sale, idx) => (
                    <tr key={idx} className="border-b border-slate-200 text-slate-700">
                      <td className="p-2">{sale.AdSoyad}</td>
                      <td className="p-2">{sale.KayKullAdSoyad}</td>
                      <td className="p-2 max-w-48 truncate">{sale.DersAdi}</td>
                      <td className="p-2 text-right text-green-400">
                        {formatCurrency(parseFloat(sale.KayitBedeli) || 0)}
                      </td>
                      <td className="p-2">{sale.KayTar?.split(" ")[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewSales.length > 50 && (
                <p className="text-center text-slate-500 py-2">... ve {previewSales.length - 50} kayıt daha</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Agents */}
      {stats?.top_agents?.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
              <Trophy className="text-amber-400" size={20} />
              En Başarılı Danışmanlar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.top_agents.map((agent, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold
                      ${idx === 0 ? 'bg-amber-500 text-black' : idx === 1 ? 'bg-slate-400 text-black' : idx === 2 ? 'bg-orange-600 text-slate-800' : 'bg-slate-300 text-slate-700'}
                    `}>
                      {idx + 1}
                    </span>
                    <span className="text-slate-800">{agent.agent_name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-semibold">{formatCurrency(agent.total_amount)}</p>
                    <p className="text-slate-500 text-xs">{agent.sale_count} satış</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Synced Sales List */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-lg text-slate-800">Senkronize Edilmiş Satışlar</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <Input
                  type="text"
                  placeholder="Danışman ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchSyncedSales(1)}
                  className="pl-9 bg-white border-slate-300 text-slate-800 w-48"
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => fetchSyncedSales(1)} className="border-slate-300">
                <Search size={16} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-cyan-400" size={24} />
            </div>
          ) : sales.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Database size={48} className="mx-auto mb-2 opacity-50" />
              <p>Henüz senkronize edilmiş satış yok</p>
              <p className="text-sm">Yukarıdan tarih seçip senkronize edin</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3">Müşteri</th>
                      <th className="text-left p-3">Danışman</th>
                      <th className="text-left p-3">Ders</th>
                      <th className="text-left p-3">Sertifika</th>
                      <th className="text-right p-3">Tutar</th>
                      <th className="text-left p-3">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale) => (
                      <tr key={sale.id} className="border-b border-slate-200 text-slate-700 hover:bg-slate-50">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{sale.customer_name}</p>
                            <p className="text-xs text-slate-500">{sale.phone}</p>
                          </div>
                        </td>
                        <td className="p-3">{sale.agent_name}</td>
                        <td className="p-3 max-w-48 truncate" title={sale.course_name}>{sale.course_name}</td>
                        <td className="p-3">
                          <span className="px-2 py-1 bg-slate-200 rounded text-xs">{sale.certificate_type}</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className={sale.amount > 0 ? 'text-green-400 font-semibold' : 'text-slate-500'}>
                            {formatCurrency(sale.amount)}
                          </span>
                        </td>
                        <td className="p-3 text-slate-500">{formatDate(sale.sale_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => fetchSyncedSales(currentPage - 1)}
                    className="border-slate-300"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-slate-500 text-sm">
                    Sayfa {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => fetchSyncedSales(currentPage + 1)}
                    className="border-slate-300"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Silinen Kayıtlar Bölümü */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
              <Trash2 className="text-red-400" size={20} />
              Silinen Kayıtlar Arşivi
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setShowDeleted(!showDeleted);
                if (!showDeleted) fetchDeletedSales();
              }}
              className="border-slate-300"
            >
              {showDeleted ? 'Gizle' : 'Göster'}
            </Button>
          </div>
          {deletedStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-slate-500 text-xs">Toplam Silinen</p>
                <p className="text-red-400 font-bold text-lg">{deletedStats.total_deleted}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-slate-500 text-xs">Silinen Tutar</p>
                <p className="text-red-400 font-bold text-lg">{formatCurrency(deletedStats.total_amount)}</p>
              </div>
              {deletedStats.monthly_stats?.[0] && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-slate-500 text-xs">Bu Ay Silinen</p>
                  <p className="text-amber-400 font-bold text-lg">
                    {deletedStats.monthly_stats[0].count} ({formatCurrency(deletedStats.monthly_stats[0].amount)})
                  </p>
                </div>
              )}
              {deletedStats.agent_stats?.[0] && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-slate-500 text-xs">En Çok Silinen</p>
                  <p className="text-slate-700 font-semibold truncate">{deletedStats.agent_stats[0].agent_name}</p>
                  <p className="text-slate-500 text-xs">{deletedStats.agent_stats[0].count} kayıt</p>
                </div>
              )}
            </div>
          )}
        </CardHeader>
        
        {showDeleted && (
          <CardContent>
            {deletedSales.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Trash2 size={48} className="mx-auto mb-2 opacity-50" />
                <p>Silinen kayıt bulunamadı</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-3">Müşteri</th>
                        <th className="text-left p-3">Danışman</th>
                        <th className="text-left p-3">Ders</th>
                        <th className="text-right p-3">Tutar</th>
                        <th className="text-left p-3">Satış Tarihi</th>
                        <th className="text-left p-3">Silinme Tarihi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deletedSales.map((sale) => (
                        <tr key={sale.id} className="border-b border-slate-200 text-slate-700 hover:bg-red-900/10">
                          <td className="p-3">{sale.customer_name}</td>
                          <td className="p-3">{sale.agent_name}</td>
                          <td className="p-3 max-w-48 truncate">{sale.course_name}</td>
                          <td className="p-3 text-right text-red-400">{formatCurrency(sale.amount)}</td>
                          <td className="p-3 text-slate-500">{sale.original_date?.split(" ")[0]}</td>
                          <td className="p-3 text-slate-500">{formatDate(sale.deleted_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {deletedTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={deletedPage === 1}
                      onClick={() => fetchDeletedSales(deletedPage - 1)}
                      className="border-slate-300"
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="text-slate-500 text-sm">
                      Sayfa {deletedPage} / {deletedTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={deletedPage === deletedTotalPages}
                      onClick={() => fetchDeletedSales(deletedPage + 1)}
                      className="border-slate-300"
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

// Students Section - Öğrenci Yönetimi
const StudentsSection = () => {
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  // Filters
  const [agentFilter, setAgentFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [filterOptions, setFilterOptions] = useState({ agents: [], courses: [] });

  const fetchStudents = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (searchTerm) params.append("search", searchTerm);
      if (agentFilter && agentFilter !== "all") params.append("agent_name", agentFilter);
      if (courseFilter && courseFilter !== "all") params.append("course", courseFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (paymentFilter && paymentFilter !== "all") params.append("payment_status", paymentFilter);
      
      const response = await authAxios.get(`${API}/students?${params}`);
      setStudents(response.data.students || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total || 0);
      setCurrentPage(page);
    } catch (error) {
      toast.error("Ogrenciler yuklenirken hata olustu");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await authAxios.get(`${API}/students/filters`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error("Filtre secenekleri yuklenemedi:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await authAxios.get(`${API}/students/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Stats yüklenirken hata:", error);
    }
  };

  const fetchStudentDetail = async (studentName) => {
    try {
      setDetailLoading(true);
      const encodedName = encodeURIComponent(studentName);
      const response = await authAxios.get(`${API}/students/${encodedName}/detail`);
      setStudentDetail(response.data);
    } catch (error) {
      toast.error("Öğrenci detayları yüklenirken hata oluştu");
    } finally {
      setDetailLoading(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedStudent) return;
    
    try {
      const encodedName = encodeURIComponent(selectedStudent);
      await authAxios.post(`${API}/students/${encodedName}/notes`, { note: newNote });
      toast.success("Not eklendi");
      setNewNote("");
      fetchStudentDetail(selectedStudent);
    } catch (error) {
      toast.error("Not eklenirken hata oluştu");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Bu notu silmek istediğinizden emin misiniz?")) return;
    
    try {
      await authAxios.delete(`${API}/students/notes/${noteId}`);
      toast.success("Not silindi");
      if (selectedStudent) fetchStudentDetail(selectedStudent);
    } catch (error) {
      toast.error("Not silinirken hata oluştu");
    }
  };

  const openStudentDetail = (studentName) => {
    setSelectedStudent(studentName);
    fetchStudentDetail(studentName);
  };

  const closeStudentDetail = () => {
    setSelectedStudent(null);
    setStudentDetail(null);
  };

  useEffect(() => {
    fetchStudents();
    fetchStats();
    fetchFilters();
  }, []);

  // Filtre değiştiğinde yeniden yükle
  useEffect(() => {
    fetchStudents(1);
  }, [agentFilter, courseFilter, dateFrom, dateTo, paymentFilter]);

  const clearFilters = () => {
    setSearchTerm("");
    setAgentFilter("");
    setCourseFilter("");
    setDateFrom("");
    setDateTo("");
    setPaymentFilter("");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      if (dateStr.includes("T")) {
        return new Date(dateStr).toLocaleDateString('tr-TR');
      }
      return dateStr.split(" ")[0];
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="students-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <GraduationCap className="text-indigo-400" />
            Öğrenciler
          </h2>
          <p className="text-slate-500 mt-1">Kayıtlı öğrencileri ve satın alma geçmişlerini görüntüleyin</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/10 rounded-lg">
                  <GraduationCap className="text-indigo-400" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Toplam Öğrenci</p>
                  <p className="text-slate-800 text-xl font-bold">{stats.total_students?.toLocaleString('tr-TR')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <TrendingUp className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Bu Ay Kayıt</p>
                  <p className="text-slate-800 text-xl font-bold">{stats.this_month_students}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {stats.popular_courses?.[0] && (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500/10 rounded-lg">
                    <BookOpen className="text-amber-400" size={20} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Popüler Kurs</p>
                    <p className="text-slate-800 text-sm font-semibold truncate">{stats.popular_courses[0].course}</p>
                    <p className="text-slate-500 text-xs">{stats.popular_courses[0].student_count} öğrenci</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {stats.top_spenders?.[0] && (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Crown className="text-purple-400" size={20} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">En Çok Harcayan</p>
                    <p className="text-slate-800 text-sm font-semibold truncate">{stats.top_spenders[0].name}</p>
                    <p className="text-green-400 text-xs">{formatCurrency(stats.top_spenders[0].total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4 space-y-3">
          {/* Row 1: Search + Agent + Course */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Arama (isim/telefon)</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                <Input
                  type="text"
                  placeholder="Ogrenci ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchStudents(1)}
                  className="pl-9 bg-white border-slate-300 text-slate-800 h-9"
                  data-testid="student-search"
                />
              </div>
            </div>
            <div className="w-48">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Danisma</label>
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger className="bg-white border-slate-300 h-9 text-slate-800" data-testid="student-agent-filter">
                  <SelectValue placeholder="Tumu" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800 max-h-60">
                  <SelectItem value="all">Tum Danismanlar</SelectItem>
                  {filterOptions.agents.map(a => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Kurs</label>
              <Select value={courseFilter} onValueChange={setCourseFilter}>
                <SelectTrigger className="bg-white border-slate-300 h-9 text-slate-800" data-testid="student-course-filter">
                  <SelectValue placeholder="Tumu" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800 max-h-60">
                  <SelectItem value="all">Tum Kurslar</SelectItem>
                  {filterOptions.courses.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Row 2: Date range + Payment + Actions */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Baslangic Tarihi</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-white border-slate-300 text-slate-800 h-9"
                data-testid="student-date-from"
              />
            </div>
            <div className="w-40">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Bitis Tarihi</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-white border-slate-300 text-slate-800 h-9"
                data-testid="student-date-to"
              />
            </div>
            <div className="w-44">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Odeme Durumu</label>
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="bg-white border-slate-300 h-9 text-slate-800" data-testid="student-payment-filter">
                  <SelectValue placeholder="Tumu" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  <SelectItem value="all">Tumu</SelectItem>
                  <SelectItem value="paid">Odemis</SelectItem>
                  <SelectItem value="unpaid">Odenmemis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => fetchStudents(1)} className="bg-indigo-600 hover:bg-indigo-700 h-9" data-testid="student-search-btn">
              <Search size={15} className="mr-1" /> Filtrele
            </Button>
            <Button variant="outline" onClick={clearFilters} className="border-slate-300 text-slate-600 h-9" data-testid="student-clear-filters">
              <XCircle size={15} className="mr-1" /> Temizle
            </Button>
            {totalCount > 0 && (
              <span className="text-xs text-slate-500 ml-auto">{totalCount} ogrenci bulundu</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Students List */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800">Öğrenci Listesi</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-indigo-400" size={24} />
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <GraduationCap size={48} className="mx-auto mb-2 opacity-50" />
              <p>Öğrenci bulunamadı</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3">Öğrenci</th>
                      <th className="text-left p-3">Telefon</th>
                      <th className="text-center p-3">Kurs Sayısı</th>
                      <th className="text-right p-3">Toplam Harcama</th>
                      <th className="text-left p-3">İlk Kayıt</th>
                      <th className="text-left p-3">Danışman(lar)</th>
                      <th className="text-center p-3">Notlar</th>
                      <th className="text-center p-3">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student.id} className="border-b border-slate-200 text-slate-700 hover:bg-slate-50">
                        <td className="p-3">
                          <p className="font-medium">{student.name}</p>
                        </td>
                        <td className="p-3 text-slate-500">{student.phone || "-"}</td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded text-xs">
                            {student.course_count} kurs
                          </span>
                        </td>
                        <td className="p-3 text-right text-green-400 font-semibold">
                          {formatCurrency(student.total_spent)}
                        </td>
                        <td className="p-3 text-slate-500">{formatDate(student.first_purchase)}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {student.agents?.slice(0, 2).map((agent, idx) => (
                              <span key={idx} className="px-1.5 py-0.5 bg-slate-200 rounded text-xs">{agent}</span>
                            ))}
                            {student.agents?.length > 2 && (
                              <span className="text-slate-500 text-xs">+{student.agents.length - 2}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          {student.note_count > 0 && (
                            <span className="px-2 py-1 bg-amber-500/20 text-amber-300 rounded text-xs">
                              {student.note_count}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openStudentDetail(student.name)}
                            className="border-slate-300 text-slate-700 hover:bg-indigo-600 hover:text-slate-800 hover:border-indigo-600"
                          >
                            <Eye size={14} className="mr-1" /> Detay
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => fetchStudents(currentPage - 1)}
                    className="border-slate-300"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-slate-500 text-sm">
                    Sayfa {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => fetchStudents(currentPage + 1)}
                    className="border-slate-300"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Student Detail Modal */}
      {selectedStudent && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/20 rounded-lg">
                  <User className="text-indigo-400" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">{selectedStudent}</h3>
                  <p className="text-slate-500 text-sm">Öğrenci Detayları</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={closeStudentDetail}>
                <X size={20} />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-4 space-y-4">
              {detailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="animate-spin text-indigo-400" size={24} />
                </div>
              ) : studentDetail ? (
                <>
                  {/* Student Info Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <p className="text-slate-500 text-xs">Telefon</p>
                      <p className="text-slate-800 font-semibold">{studentDetail.student.phone || "-"}</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <p className="text-slate-500 text-xs">Toplam Harcama</p>
                      <p className="text-green-400 font-bold">{formatCurrency(studentDetail.student.total_spent)}</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <p className="text-slate-500 text-xs">Kurs Sayısı</p>
                      <p className="text-slate-800 font-semibold">{studentDetail.student.course_count}</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <p className="text-slate-500 text-xs">İlk Kayıt</p>
                      <p className="text-slate-800 font-semibold">{studentDetail.student.registration_date?.split(" ")[0] || "-"}</p>
                    </div>
                  </div>

                  {/* Certificates & Courses */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="text-slate-800 font-semibold mb-2 flex items-center gap-2">
                        <Award className="text-amber-400" size={16} /> Sertifikalar
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {studentDetail.student.certificates?.length > 0 ? (
                          studentDetail.student.certificates.map((cert, idx) => (
                            <span key={idx} className="px-2 py-1 bg-amber-500/20 text-amber-300 rounded text-sm">
                              {cert}
                            </span>
                          ))
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="text-slate-800 font-semibold mb-2 flex items-center gap-2">
                        <BookOpen className="text-indigo-400" size={16} /> Kurslar
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {studentDetail.student.courses?.map((course, idx) => (
                          <span key={idx} className="px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded text-sm">
                            {course}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Purchase History */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-slate-800 font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="text-green-400" size={16} /> Satın Alma Geçmişi
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {studentDetail.purchases?.map((purchase, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white rounded">
                          <div>
                            <p className="text-slate-800 text-sm">{purchase.course_name}</p>
                            <p className="text-slate-500 text-xs">{purchase.certificate_type} • {purchase.agent_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-400 font-semibold">{formatCurrency(purchase.amount)}</p>
                            <p className="text-slate-500 text-xs">{purchase.original_date?.split(" ")[0]}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-slate-800 font-semibold mb-3 flex items-center gap-2">
                      <MessageSquare className="text-cyan-400" size={16} /> Notlar
                    </h4>
                    
                    {/* Add Note */}
                    <div className="flex gap-2 mb-3">
                      <Input
                        type="text"
                        placeholder="Yeni not ekle..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addNote()}
                        className="bg-slate-50 border-slate-300 text-slate-800 flex-1"
                      />
                      <Button onClick={addNote} className="bg-cyan-600 hover:bg-cyan-700">
                        <Plus size={16} />
                      </Button>
                    </div>

                    {/* Notes List */}
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {studentDetail.notes?.length > 0 ? (
                        studentDetail.notes.map((note) => (
                          <div key={note.id} className="flex items-start justify-between p-2 bg-white rounded group">
                            <div>
                              <p className="text-slate-800 text-sm">{note.note}</p>
                              <p className="text-slate-500 text-xs">{note.created_by} • {formatDate(note.created_at)}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteNote(note.id)}
                              className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-center py-2">Henüz not yok</p>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Leads Section - Müşteri Adayları (Data)
const LeadsSection = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '.'),
    end: new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '.')
  });

  const fetchLeads = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 30 });
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter) params.append("status", statusFilter);
      if (agentFilter) params.append("agent_name", agentFilter);
      
      const response = await authAxios.get(`${API}/kariyer-paneli/data/list?${params}`);
      setLeads(response.data.data || []);
      setTotalPages(response.data.total_pages || 1);
      setCurrentPage(page);
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await authAxios.get(`${API}/kariyer-paneli/data/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Stats yüklenirken hata:", error);
    }
  };

  const syncData = async () => {
    try {
      setSyncing(true);
      const response = await authAxios.post(`${API}/kariyer-paneli/data/sync`, {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      toast.success(response.data.message);
      fetchStats();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Senkronizasyon başarısız");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchLeads();
  }, []);

  const getStatusColor = (status) => {
    if (!status) return "bg-slate-200 text-slate-600";
    const s = status.toLowerCase();
    if (s.includes("satış") || s.includes("kayıt")) return "bg-green-100 text-green-700";
    if (s.includes("ilgisiz") || s.includes("ulaşılamadı")) return "bg-red-100 text-red-700";
    if (s.includes("düşünüyor") || s.includes("araştırıyor")) return "bg-amber-100 text-amber-700";
    if (s.includes("görüşme") || s.includes("bilgi")) return "bg-blue-100 text-blue-700";
    return "bg-slate-200 text-slate-600";
  };

  return (
    <div className="space-y-6" data-testid="leads-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Users className="text-orange-400" />
            Müşteri Adayları (Data)
          </h2>
          <p className="text-slate-500 mt-1">KariyerPaneli'den çekilen görüşme kayıtları</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Users className="text-orange-400" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Toplam Kayıt</p>
                  <p className="text-slate-800 text-xl font-bold">{stats.total?.toLocaleString('tr-TR')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {stats.status_distribution?.[0] && (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Activity className="text-blue-400" size={20} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">En Çok Durum</p>
                    <p className="text-slate-800 text-sm font-semibold truncate">{stats.status_distribution[0].status}</p>
                    <p className="text-slate-500 text-xs">{stats.status_distribution[0].count} kayıt</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {stats.agent_stats?.[0] && (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Trophy className="text-purple-400" size={20} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">En Aktif Danışman</p>
                    <p className="text-slate-800 text-sm font-semibold truncate">{stats.agent_stats[0].agent}</p>
                    <p className="text-slate-500 text-xs">{stats.agent_stats[0].count} görüşme</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {stats.course_demand?.[0] && (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <BookOpen className="text-green-400" size={20} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">En Çok Talep</p>
                    <p className="text-slate-800 text-sm font-semibold truncate">{stats.course_demand[0].course}</p>
                    <p className="text-slate-500 text-xs">{stats.course_demand[0].count} talep</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Sync Controls */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
            <RefreshCw className="text-orange-400" size={20} />
            Data Senkronizasyonu
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-500">Başlangıç</Label>
              <Input
                type="text"
                placeholder="01.03.2025"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="bg-white border-slate-300 text-slate-800"
              />
            </div>
            <div>
              <Label className="text-slate-500">Bitiş</Label>
              <Input
                type="text"
                placeholder="31.03.2025"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="bg-white border-slate-300 text-slate-800"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button 
                onClick={syncData} 
                disabled={syncing}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                {syncing ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Download className="mr-2" size={16} />}
                Data Senkronize Et
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <Input
                type="text"
                placeholder="İsim veya telefon ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchLeads(1)}
                className="pl-9 bg-white border-slate-300 text-slate-800"
              />
            </div>
            <Input
              type="text"
              placeholder="Durum filtrele..."
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white border-slate-300 text-slate-800"
            />
            <Input
              type="text"
              placeholder="Danışman filtrele..."
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="bg-white border-slate-300 text-slate-800"
            />
            <Button onClick={() => fetchLeads(1)} className="bg-orange-600 hover:bg-orange-700">
              <Filter size={16} className="mr-2" /> Filtrele
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads List */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800">Görüşme Kayıtları</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-orange-400" size={24} />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users size={48} className="mx-auto mb-2 opacity-50" />
              <p>Kayıt bulunamadı</p>
              <p className="text-sm">Yukarıdan tarih seçip senkronize edin</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3">Müşteri</th>
                      <th className="text-left p-3">Telefon</th>
                      <th className="text-left p-3">Kurs</th>
                      <th className="text-left p-3">Danışman</th>
                      <th className="text-left p-3">Durum</th>
                      <th className="text-left p-3">Not</th>
                      <th className="text-left p-3">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id || lead.unique_key} className="border-b border-slate-200 text-slate-700 hover:bg-slate-50">
                        <td className="p-3">
                          <p className="font-medium">{lead.customer_name}</p>
                          <p className="text-xs text-slate-500">{lead.education_level}</p>
                        </td>
                        <td className="p-3 text-slate-500">{lead.phone}</td>
                        <td className="p-3 max-w-32 truncate" title={lead.course_name}>{lead.course_name || "-"}</td>
                        <td className="p-3">{lead.agent_name}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(lead.status)}`}>
                            {lead.status || "-"}
                          </span>
                        </td>
                        <td className="p-3 max-w-48 truncate text-slate-500" title={lead.note}>
                          {lead.note || "-"}
                        </td>
                        <td className="p-3 text-slate-500 text-xs">{lead.record_date?.split(" ")[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => fetchLeads(currentPage - 1)}
                    className="border-slate-300"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-slate-500 text-sm">
                    Sayfa {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => fetchLeads(currentPage + 1)}
                    className="border-slate-300"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Status Distribution */}
      {stats?.status_distribution?.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-800">Durum Dağılımı</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {stats.status_distribution.map((item, idx) => (
                <div key={idx} className="p-3 bg-slate-100 rounded-lg text-center">
                  <p className="text-slate-800 font-bold text-lg">{item.count}</p>
                  <p className="text-slate-500 text-xs truncate" title={item.status}>{item.status}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Sales Section - Gelişmiş satış yönetimi
const SalesSection = () => {
  const [agents, setAgents] = useState([]);
  const [saleData, setSaleData] = useState({ agent_id: "", amount: "", description: "" });
  const [allSales, setAllSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState("month");
  const [searchTerm, setSearchTerm] = useState("");

  const loadSales = useCallback(async () => {
    setLoading(true);
    try {
      let dateFrom = null, dateTo = null;
      const now = new Date();
      
      if (dateFilter === "today") {
        dateFrom = dateTo = now.toISOString().split('T')[0];
      } else if (dateFilter === "week") {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        dateFrom = weekAgo.toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
      } else if (dateFilter === "month") {
        dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
      }
      
      let url = `${API}/sales?limit=500`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const res = await authAxios.get(url);
      setAllSales(res.data || []);
    } catch (error) {
      console.error("Sales load error:", error);
      toast.error("Satışlar yüklenemedi");
    }
    setLoading(false);
  }, [dateFilter]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await authAxios.get(`${API}/agents`);
        setAgents(res.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchAgents();
  }, []);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!saleData.agent_id || !saleData.amount) {
      toast.error("Çalışan ve tutar seçilmeli");
      return;
    }
    try {
      await authAxios.post(`${API}/sales`, {
        agent_id: saleData.agent_id,
        amount: parseFloat(saleData.amount),
        description: saleData.description
      });
      toast.success("Satış kaydedildi! 🎉");
      setSaleData({ agent_id: "", amount: "", description: "" });
      loadSales();
    } catch (error) {
      toast.error("Satış kaydedilemedi");
    }
  };

  const deleteSale = async (saleId) => {
    if (!window.confirm("Bu satışı silmek istediğinize emin misiniz?")) return;
    try {
      await authAxios.delete(`${API}/sales/${saleId}`);
      toast.success("Satış silindi");
      loadSales();
    } catch (error) {
      toast.error("Satış silinemedi");
    }
  };

  const filteredSales = allSales.filter(s => 
    !searchTerm || 
    s.agent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalRevenue = filteredSales.reduce((sum, s) => sum + (s.amount || 0), 0);

  return (
    <div data-testid="sales-section" className="space-y-6">
      <PageHeader 
        icon={DollarSign} 
        title="Satışlar" 
        description="Satış kayıtlarını yönetin"
        color="bg-amber-500/10 text-amber-400"
      />
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 rounded-xl bg-emerald-500/20">
              <DollarSign size={24} className="text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">₺{totalRevenue.toLocaleString('tr-TR')}</p>
              <p className="text-sm text-slate-500">Toplam Ciro</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <TrendingUp size={24} className="text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{filteredSales.length}</p>
              <p className="text-sm text-slate-500">Toplam Satış</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 rounded-xl bg-purple-500/20">
              <BarChart3 size={24} className="text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">₺{filteredSales.length > 0 ? Math.round(totalRevenue / filteredSales.length).toLocaleString('tr-TR') : 0}</p>
              <p className="text-sm text-slate-500">Ortalama</p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Yeni Satış Formu */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus size={18} className="text-emerald-400" />
              Yeni Satış
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Çalışan</Label>
                <Select value={saleData.agent_id} onValueChange={(v) => setSaleData({ ...saleData, agent_id: v })}>
                  <SelectTrigger className="bg-white border-slate-300">
                    <SelectValue placeholder="Seçin..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    {agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Tutar</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₺</span>
                  <Input
                    type="number"
                    placeholder="0"
                    value={saleData.amount}
                    onChange={(e) => setSaleData({ ...saleData, amount: e.target.value })}
                    className="pl-8 bg-white border-slate-300"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Input
                  placeholder="Opsiyonel..."
                  value={saleData.description}
                  onChange={(e) => setSaleData({ ...saleData, description: e.target.value })}
                  className="bg-white border-slate-300"
                />
              </div>
              
              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-500">
                <DollarSign size={18} className="mr-2" />
                Kaydet
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Satış Listesi */}
        <Card className="bg-white border-slate-200 lg:col-span-2">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp size={18} className="text-emerald-400" />
                Satış Listesi
              </CardTitle>
              <div className="flex gap-2">
                <Input
                  placeholder="Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-40 bg-white border-slate-300"
                />
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-32 bg-white border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    <SelectItem value="today">Bugün</SelectItem>
                    <SelectItem value="week">Bu Hafta</SelectItem>
                    <SelectItem value="month">Bu Ay</SelectItem>
                    <SelectItem value="all">Tümü</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={loadSales}>
                  <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[500px] overflow-y-auto space-y-2">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Yükleniyor...</div>
              ) : filteredSales.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <DollarSign size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Satış bulunamadı</p>
                </div>
              ) : (
                filteredSales.map((sale) => (
                  <div key={sale.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors">
                    <div className="p-2 rounded-lg bg-emerald-500/10">
                      <DollarSign size={16} className="text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{sale.agent_name}</p>
                      <p className="text-xs text-slate-500">
                        {sale.customer_name && `${sale.customer_name} • `}
                        {new Date(sale.timestamp).toLocaleString('tr-TR')}
                      </p>
                    </div>
                    <span className="font-mono font-bold text-emerald-400 whitespace-nowrap">
                      ₺{(sale.amount || 0).toLocaleString('tr-TR')}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => deleteSale(sale.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Calls Section
const CallsSection = () => {
  const [agents, setAgents] = useState([]);
  const [callData, setCallData] = useState({ agent_id: "", duration_seconds: "", is_successful: false });

  useEffect(() => {
    authAxios.get(`${API}/agents`).then(res => setAgents(res.data)).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!callData.agent_id) {
      toast.error("Çalışan seçilmeli");
      return;
    }
    try {
      await authAxios.post(`${API}/calls`, {
        agent_id: callData.agent_id,
        duration_seconds: parseInt(callData.duration_seconds) || 0,
        is_successful: callData.is_successful
      });
      toast.success("Arama kaydedildi!");
      setCallData({ agent_id: "", duration_seconds: "", is_successful: false });
    } catch (error) {
      toast.error("Arama kaydedilemedi");
    }
  };

  return (
    <div data-testid="calls-section">
      <PageHeader 
        icon={Phone} 
        title="Arama Girişi" 
        description="Arama kaydı oluşturun"
        color="bg-blue-500/10 text-blue-400"
      />
      
      <Card className="bg-white border-slate-200 max-w-xl">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label>Çalışan</Label>
              <Select value={callData.agent_id} onValueChange={(v) => setCallData({ ...callData, agent_id: v })}>
                <SelectTrigger className="bg-white border-slate-300" data-testid="call-agent-select">
                  <SelectValue placeholder="Çalışan seçin..." />
                </SelectTrigger>
                <SelectContent className="bg-white text-slate-800 border-slate-300">
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Arama Süresi (saniye)</Label>
              <Input
                type="number"
                placeholder="0"
                value={callData.duration_seconds}
                onChange={(e) => setCallData({ ...callData, duration_seconds: e.target.value })}
                className="bg-white border-slate-300"
                data-testid="call-duration-input"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-100">
              <div>
                <Label>Başarılı Arama</Label>
                <p className="text-sm text-slate-500">Satışla sonuçlandı mı?</p>
              </div>
              <Switch
                checked={callData.is_successful}
                onCheckedChange={(checked) => setCallData({ ...callData, is_successful: checked })}
                data-testid="call-successful-switch"
              />
            </div>
            
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 h-12" data-testid="submit-call-btn">
              <Phone size={18} className="mr-2" />
              Aramayı Kaydet
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Excel Upload Section
const ExcelSection = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const fileInputRef = React.useRef(null);

  const parseExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          // Parse TSV (tab-separated, UTF-16)
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error('Dosyada veri bulunamadı'));
            return;
          }

          const headers = lines[0].split('\t').map(h => h.trim());
          const data = [];

          // Find column indices
          const colMap = {
            adSoyad: headers.findIndex(h => h.toLowerCase().includes('adsoyad') || h === 'AdSoyad'),
            kayKullAdSoyad: headers.findIndex(h => h.toLowerCase().includes('kaykulladsoyad') || h === 'KayKullAdSoyad'),
            toplamBorc: headers.findIndex(h => h.toLowerCase().includes('toplamborc') || h === 'ToplamBorc'),
            toplamOdenen: headers.findIndex(h => h.toLowerCase().includes('toplamoden') || h === 'ToplamOdenen'),
            kayTar: headers.findIndex(h => h.toLowerCase().includes('kaytar') || h === 'KayTar'),
          };

          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split('\t').map(c => c.trim());
            
            const agentName = cols[colMap.kayKullAdSoyad] || '';
            const amount = parseFloat(cols[colMap.toplamBorc]?.replace(',', '.')) || 0;
            const collected = parseFloat(cols[colMap.toplamOdenen]?.replace(',', '.')) || 0;
            const customerName = cols[colMap.adSoyad] || '';
            const dateStr = cols[colMap.kayTar] || '';

            // Skip invalid rows
            if (!agentName || agentName === 'Referans' || agentName === 'ınsta' || amount <= 0) continue;

            // Parse date (format: "27.02.2026 16:57:40")
            let timestamp = '';
            try {
              const [datePart, timePart] = dateStr.split(' ');
              const [day, month, year] = datePart.split('.');
              timestamp = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart || '12:00:00'}`;
            } catch {
              timestamp = new Date().toISOString();
            }

            data.push({
              agent_name: agentName,
              amount: amount,
              collected_amount: collected,
              customer_name: customerName,
              timestamp: timestamp,
              description: ''
            });
          }

          resolve(data);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file, 'UTF-16');
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file extension
    if (!file.name.match(/\.(xls|xlsx|csv)$/i)) {
      toast.error('Sadece Excel dosyaları (.xls, .xlsx, .csv) yüklenebilir');
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const data = await parseExcelFile(file);
      setPreviewData(data);
      toast.success(`${data.length} satış verisi bulundu`);
    } catch (error) {
      toast.error(`Dosya okunamadı: ${error.message}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!previewData || previewData.length === 0) {
      toast.error('Yüklenecek veri yok');
      return;
    }

    setIsUploading(true);
    try {
      const response = await authAxios.post(`${API}/sales/bulk-import`, previewData);
      setUploadResult(response.data);
      setPreviewData(null);
      toast.success(`${response.data.imported} satış başarıyla yüklendi! 🎉`);
    } catch (error) {
      toast.error('Yükleme başarısız');
    } finally {
      setIsUploading(false);
    }
  };

  // Group preview data by agent
  const groupedPreview = previewData ? previewData.reduce((acc, sale) => {
    if (!acc[sale.agent_name]) {
      acc[sale.agent_name] = { count: 0, total: 0, collected: 0 };
    }
    acc[sale.agent_name].count++;
    acc[sale.agent_name].total += sale.amount;
    acc[sale.agent_name].collected += sale.collected_amount;
    return acc;
  }, {}) : {};

  return (
    <div data-testid="excel-section">
      <PageHeader 
        icon={FileSpreadsheet} 
        title="Excel Yükle" 
        description="Toplu satış verisi yükleyin"
        color="bg-green-500/10 text-green-400"
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Card */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload size={18} className="text-green-400" />
              Dosya Yükle
            </CardTitle>
            <CardDescription>
              CRM'den export edilen Excel dosyasını seçin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="excel-file-input"
            />
            
            <Button
              variant="outline"
              className="w-full h-32 border-dashed border-2 border-slate-300 hover:border-green-500/50 flex flex-col gap-3"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                    <FileSpreadsheet size={32} className="text-green-400" />
                  </motion.div>
                  <span className="text-slate-500">İşleniyor...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet size={32} className="text-slate-500" />
                  <span className="text-slate-500">Excel Dosyası Seç</span>
                  <span className="text-xs text-slate-400">.xls, .xlsx, .csv</span>
                </>
              )}
            </Button>

            {/* Expected Format Info */}
            <div className="p-4 rounded-xl bg-slate-100 text-sm">
              <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <FileText size={14} className="text-blue-400" />
                Beklenen Sütunlar
              </h4>
              <ul className="space-y-1 text-slate-500">
                <li>• <span className="text-emerald-400">KayKullAdSoyad</span> - Satışçı adı</li>
                <li>• <span className="text-amber-400">ToplamBorc</span> - Satış tutarı (₺)</li>
                <li>• <span className="text-purple-400">ToplamOdenen</span> - Tahsil edilen (₺)</li>
                <li>• <span className="text-blue-400">AdSoyad</span> - Müşteri adı</li>
                <li>• <span className="text-rose-400">KayTar</span> - Tarih</li>
              </ul>
            </div>

            {/* Upload Result */}
            {uploadResult && (
              <div className={`p-4 rounded-xl ${uploadResult.errors?.length > 0 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {uploadResult.errors?.length > 0 ? (
                    <AlertCircle size={18} className="text-amber-400" />
                  ) : (
                    <CheckCircle size={18} className="text-emerald-400" />
                  )}
                  <span className="font-semibold text-slate-800">Yükleme Tamamlandı</span>
                </div>
                <p className="text-sm text-slate-700">
                  <span className="text-emerald-400 font-bold">{uploadResult.imported}</span> satış yüklendi
                  {uploadResult.errors?.length > 0 && (
                    <span className="text-amber-400 ml-2">({uploadResult.errors.length} hata)</span>
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={18} className="text-amber-400" />
              Önizleme
            </CardTitle>
            <CardDescription>
              {previewData ? `${previewData.length} satış yüklenmeye hazır` : 'Dosya seçilmedi'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {previewData && Object.keys(groupedPreview).length > 0 ? (
              <div className="space-y-4">
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-2">
                    {Object.entries(groupedPreview)
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([agent, data]) => (
                        <div key={agent} className="p-3 rounded-lg bg-slate-100 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center font-bold text-emerald-400">
                            {agent.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{agent}</p>
                            <p className="text-xs text-slate-500">{data.count} satış</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-amber-400">₺{data.total.toLocaleString()}</p>
                            <p className="text-xs text-slate-500">Tahsil: ₺{data.collected.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </ScrollArea>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-300"
                    onClick={() => setPreviewData(null)}
                  >
                    <X size={16} className="mr-2" />
                    İptal
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-500"
                    onClick={handleUpload}
                    disabled={isUploading}
                    data-testid="upload-excel-btn"
                  >
                    <Upload size={16} className="mr-2" />
                    {isUploading ? 'Yükleniyor...' : 'Yükle'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <FileSpreadsheet size={48} className="mx-auto mb-4 opacity-50" />
                <p>Excel dosyası seçin</p>
                <p className="text-sm mt-1">Veriler burada önizlenecek</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Excel Calls Upload Section - Supports ALL columns
const ExcelCallsSection = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const fileInputRef = React.useRef(null);

  const parseCallsExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error('Dosyada veri bulunamadı'));
            return;
          }

          const headers = lines[0].split('\t').map(h => h.trim());
          const data = [];

          // Map ALL columns
          const colMap = {
            dataDurum: headers.findIndex(h => h.includes('DataDurumAdi') || h === 'DataDurumAdi'),
            danisman: headers.findIndex(h => h.includes('DanismanAdSoyad') || h === 'DanismanAdSoyad'),
            aciklama: headers.findIndex(h => h.includes('Aciklama') || h === 'Aciklama'),
            kayTar: headers.findIndex(h => h.includes('KayTar') || h === 'KayTar'),
            adSoyad: headers.findIndex(h => h === 'AdSoyad'),
            dersAdi: headers.findIndex(h => h.includes('DersAdi') || h === 'DersAdi'),
            egitimDurumu: headers.findIndex(h => h.includes('EgitimDurumuText') || h === 'EgitimDurumuText'),
            telefon: headers.findIndex(h => h.includes('Telefon') || h === 'Telefon'),
            tarih: headers.findIndex(h => h === 'Tarih'),
            sonDataDurum: headers.findIndex(h => h.includes('SonDataDurumAdi') || h === 'SonDataDurumAdi'),
            reklamTuru: headers.findIndex(h => h.includes('ReklamTuruAdi') || h === 'ReklamTuruAdi'),
            reklamTuruText: headers.findIndex(h => h.includes('ReklamTuruText') || h === 'ReklamTuruText'),
            dataAciklama: headers.findIndex(h => h.includes('DataAciklama') || h === 'DataAciklama'),
          };

          console.log('Column mapping:', colMap);
          console.log('Headers found:', headers);

          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split('\t').map(c => c.trim());
            
            const agentName = cols[colMap.danisman] || '';
            const status = cols[colMap.dataDurum] || '';
            const note = cols[colMap.aciklama] || '';
            const customerName = cols[colMap.adSoyad] || '';
            const dateStr = cols[colMap.kayTar] || '';
            
            // Extended fields
            const dersAdi = colMap.dersAdi >= 0 ? cols[colMap.dersAdi] || '' : '';
            const egitimDurumu = colMap.egitimDurumu >= 0 ? cols[colMap.egitimDurumu] || '' : '';
            const telefon = colMap.telefon >= 0 ? cols[colMap.telefon] || '' : '';
            const tarih = colMap.tarih >= 0 ? cols[colMap.tarih] || '' : '';
            const sonDataDurum = colMap.sonDataDurum >= 0 ? cols[colMap.sonDataDurum] || '' : '';
            const reklamTuru = colMap.reklamTuru >= 0 ? cols[colMap.reklamTuru] || '' : '';
            const reklamTuruText = colMap.reklamTuruText >= 0 ? cols[colMap.reklamTuruText] || '' : '';
            const dataAciklama = colMap.dataAciklama >= 0 ? cols[colMap.dataAciklama] || '' : '';

            if (!agentName || agentName === 'nan') continue;

            let timestamp = '';
            try {
              const [datePart, timePart] = dateStr.split(' ');
              const [day, month, year] = datePart.split('.');
              timestamp = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart || '12:00:00'}`;
            } catch {
              timestamp = new Date().toISOString();
            }

            // Clean 'nan' values
            const cleanValue = (val) => (val === 'nan' || val === 'NaN' ? '' : val);

            data.push({
              agent_name: agentName,
              status: status,
              note: cleanValue(note),
              customer_name: cleanValue(customerName),
              timestamp: timestamp,
              // Extended fields
              ders_adi: cleanValue(dersAdi),
              egitim_durumu: cleanValue(egitimDurumu),
              telefon: cleanValue(telefon),
              tarih: cleanValue(tarih),
              son_data_durum: cleanValue(sonDataDurum),
              reklam_turu: cleanValue(reklamTuru),
              reklam_turu_text: cleanValue(reklamTuruText),
              data_aciklama: cleanValue(dataAciklama)
            });
          }

          resolve(data);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file, 'UTF-16');
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadResult(null);

    try {
      const data = await parseCallsExcel(file);
      setPreviewData(data);
      toast.success(`${data.length} arama verisi bulundu`);
    } catch (error) {
      toast.error(`Dosya okunamadı: ${error.message}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!previewData || previewData.length === 0) return;

    setIsUploading(true);
    try {
      // Upload in batches
      const batchSize = 1000;
      let totalImported = 0;
      for (let i = 0; i < previewData.length; i += batchSize) {
        const batch = previewData.slice(i, i + batchSize);
        const response = await authAxios.post(`${API}/calls/bulk-import`, batch);
        totalImported += response.data.imported;
      }
      setUploadResult({ imported: totalImported, total: previewData.length });
      setPreviewData(null);
      toast.success(`${totalImported} arama başarıyla yüklendi! 🎉`);
    } catch (error) {
      toast.error('Yükleme başarısız');
    } finally {
      setIsUploading(false);
    }
  };

  // Group by status
  const statusSummary = previewData ? previewData.reduce((acc, call) => {
    acc[call.status] = (acc[call.status] || 0) + 1;
    return acc;
  }, {}) : {};

  // Group by agent
  const agentSummary = previewData ? previewData.reduce((acc, call) => {
    acc[call.agent_name] = (acc[call.agent_name] || 0) + 1;
    return acc;
  }, {}) : {};

  return (
    <div data-testid="excel-calls-section">
      <PageHeader 
        icon={Phone} 
        title="Arama Excel Yükle" 
        description="Toplu arama verisi yükleyin"
        color="bg-cyan-500/10 text-cyan-400"
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload size={18} className="text-cyan-400" />
              Arama Dosyası Yükle
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input ref={fileInputRef} type="file" accept=".xls,.xlsx,.csv" onChange={handleFileSelect} className="hidden" />
            
            <Button
              variant="outline"
              className="w-full h-32 border-dashed border-2 border-slate-300 hover:border-blue-400 flex flex-col gap-3"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}><Phone size={32} className="text-cyan-400" /></motion.div><span className="text-slate-500">İşleniyor...</span></>
              ) : (
                <><Phone size={32} className="text-slate-500" /><span className="text-slate-500">Arama Excel Seç</span></>
              )}
            </Button>

            <div className="p-4 rounded-xl bg-slate-100 text-sm">
              <h4 className="font-semibold text-slate-800 mb-2">Beklenen Sütunlar (Tümü Analiz Edilir)</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-500">
                <div>• <span className="text-cyan-400">DanismanAdSoyad</span> - Danışman</div>
                <div>• <span className="text-amber-400">DataDurumAdi</span> - Durum</div>
                <div>• <span className="text-purple-400">Aciklama</span> - Not</div>
                <div>• <span className="text-blue-400">AdSoyad</span> - Müşteri</div>
                <div>• <span className="text-rose-400">KayTar</span> - Kayıt Tarihi</div>
                <div>• <span className="text-emerald-400">DersAdi</span> - Ders/Kurs</div>
                <div>• <span className="text-pink-400">EgitimDurumuText</span> - Eğitim</div>
                <div>• <span className="text-orange-400">Telefon</span> - Telefon</div>
                <div>• <span className="text-teal-400">SonDataDurumAdi</span> - Son Durum</div>
                <div>• <span className="text-yellow-400">ReklamTuruAdi</span> - Reklam Türü</div>
                <div>• <span className="text-lime-400">ReklamTuruText</span> - Reklam Detay</div>
                <div>• <span className="text-indigo-400">DataAciklama</span> - Data Açıklama</div>
              </div>
            </div>

            {uploadResult && (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-emerald-400" />
                  <span className="font-semibold text-slate-800">Yükleme Tamamlandı</span>
                </div>
                <p className="text-sm text-slate-700 mt-1">
                  <span className="text-emerald-400 font-bold">{uploadResult.imported}</span> arama yüklendi
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle>Önizleme</CardTitle>
            <CardDescription>{previewData ? `${previewData.length} arama` : 'Dosya seçilmedi'}</CardDescription>
          </CardHeader>
          <CardContent>
            {previewData ? (
              <div className="space-y-4">
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  <p className="text-sm text-slate-500 font-medium">Durum Dağılımı:</p>
                  {Object.entries(statusSummary).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([status, count]) => (
                    <div key={status} className="flex justify-between text-sm">
                      <span className="text-slate-700">{status}</span>
                      <span className="text-cyan-400 font-mono">{count}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  <p className="text-sm text-slate-500 font-medium">Danışman Dağılımı:</p>
                  {Object.entries(agentSummary).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([agent, count]) => (
                    <div key={agent} className="flex justify-between text-sm">
                      <span className="text-slate-700">{agent}</span>
                      <span className="text-amber-400 font-mono">{count}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1 border-slate-300" onClick={() => setPreviewData(null)}><X size={16} className="mr-2" />İptal</Button>
                  <Button className="flex-1 bg-cyan-600 hover:bg-cyan-500" onClick={handleUpload} disabled={isUploading}><Upload size={16} className="mr-2" />{isUploading ? 'Yükleniyor...' : 'Yükle'}</Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Phone size={48} className="mx-auto mb-4 opacity-50" />
                <p>Arama Excel dosyası seçin</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Data Analytics Section - Comprehensive Analysis
const AnalyticsSection = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentAnalytics, setAgentAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Date filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const getDateRange = () => {
    const today = new Date();
    let from = '', to = '';
    
    switch (dateFilter) {
      case 'today':
        from = today.toISOString().split('T')[0];
        to = from;
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        from = weekAgo.toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        from = monthAgo.toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'custom':
        from = customDateFrom;
        to = customDateTo;
        break;
      default:
        from = '';
        to = '';
    }
    return { from, to };
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateFilter, customDateFrom, customDateTo]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      let url = `${API}/analytics/comprehensive`;
      if (from && to) {
        url += `?date_from=${from}&date_to=${to}`;
      }
      const res = await authAxios.get(url);
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Analiz verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentAnalytics = async (agentName) => {
    try {
      const res = await authAxios.get(`${API}/analytics/agent/${encodeURIComponent(agentName)}`);
      setAgentAnalytics(res.data);
      setSelectedAgent(agentName);
    } catch (error) {
      toast.error('Danışman analizi yüklenemedi');
    }
  };

  const getStatusIcon = (status) => {
    if (status.includes('CEVAP YOK')) return <PhoneMissed size={14} className="text-rose-400" />;
    if (status.includes('MEŞGUL')) return <PhoneOff size={14} className="text-amber-400" />;
    if (status.includes('İSTEMİYOR')) return <ThumbsDown size={14} className="text-red-400" />;
    if (status.includes('DÜŞÜNECEK')) return <Clock size={14} className="text-blue-400" />;
    return <Phone size={14} className="text-slate-500" />;
  };

  const getInsightIcon = (type) => {
    const icons = {
      warning: PhoneOff,
      info: Phone,
      opportunity: TrendingUp,
      critical: AlertCircle,
      success: Trophy,
      action: Clock
    };
    return icons[type] || Brain;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
          <BarChart3 size={48} className="text-purple-400" />
        </motion.div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Genel Bakış', icon: BarChart3 },
    { id: 'charts', label: 'Grafikler', icon: TrendingUp },
    { id: 'funnel', label: 'Dönüşüm Hunisi', icon: Filter },
    { id: 'notes', label: 'Not Analizi', icon: FileText },
    { id: 'demographics', label: 'Demografi', icon: Users },
    { id: 'comparison', label: 'Danışman Skor', icon: Gauge },
    { id: 'predictions', label: 'Tahminler', icon: TrendingUp },
    { id: 'alerts', label: 'Uyarılar', icon: Bell },
    { id: 'export', label: 'Export', icon: Download },
  ];

  return (
    <div data-testid="analytics-section">
      <PageHeader 
        icon={BarChart3} 
        title="Kapsamlı Data Analizi" 
        description="Tüm sütunların detaylı analizi ve AI destekli öneriler"
        color="bg-purple-500/10 text-purple-400"
      />

      {/* Date Filter */}
      <Card className="bg-white border-slate-200 mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-purple-400" />
              <span className="text-slate-500 text-sm">Tarih Filtresi:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'Tümü' },
                { id: 'today', label: 'Bugün' },
                { id: 'week', label: 'Son 7 Gün' },
                { id: 'month', label: 'Son 30 Gün' },
                { id: 'custom', label: 'Özel' },
              ].map((filter) => (
                <Button
                  key={filter.id}
                  variant={dateFilter === filter.id ? "default" : "outline"}
                  size="sm"
                  className={dateFilter === filter.id ? 'bg-purple-600' : 'border-slate-300'}
                  onClick={() => setDateFilter(filter.id)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-2 ml-auto">
                <Input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="w-40 bg-white border-slate-300"
                />
                <span className="text-slate-500">-</span>
                <Input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="w-40 bg-white border-slate-300"
                />
              </div>
            )}
            {dateFilter !== 'all' && (
              <div className="ml-auto text-sm text-slate-500">
                {dateFilter === 'custom' && customDateFrom && customDateTo ? (
                  <span>{customDateFrom} → {customDateTo}</span>
                ) : dateFilter === 'today' ? (
                  <span>Bugün: {new Date().toLocaleDateString('tr-TR')}</span>
                ) : dateFilter === 'week' ? (
                  <span>Son 7 gün</span>
                ) : dateFilter === 'month' ? (
                  <span>Son 30 gün</span>
                ) : null}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "default" : "outline"}
            className={`flex items-center gap-2 ${activeTab === tab.id ? 'bg-purple-600' : 'border-slate-300'}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={16} />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Overview Stats - Always visible */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-900/10 border-emerald-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Phone size={24} className="text-emerald-400" />
              <div>
                <p className="text-2xl font-bold text-slate-800">{analytics?.summary?.total_calls?.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Toplam Arama</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-900/10 border-amber-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <DollarSign size={24} className="text-amber-400" />
              <div>
                <p className="text-2xl font-bold text-slate-800">{analytics?.summary?.total_sales?.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Toplam Satış</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-900/10 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp size={24} className="text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-slate-800">%{analytics?.summary?.conversion_rate}</p>
                <p className="text-sm text-slate-500">Dönüşüm</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-900/10 border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <DollarSign size={24} className="text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-slate-800">₺{(analytics?.summary?.total_revenue || 0).toLocaleString()}</p>
                <p className="text-sm text-slate-500">Ciro</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-900/10 border-cyan-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileText size={24} className="text-cyan-400" />
              <div>
                <p className="text-2xl font-bold text-slate-800">{analytics?.summary?.notes_analyzed?.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Not Analizi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <>
          {/* AI Insights */}
          <Card className="bg-white border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain size={20} className="text-purple-400" />
                AI Destekli Öneriler
              </CardTitle>
              <CardDescription>Tüm verilerinize göre otomatik analiz ve aksiyon önerileri</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analytics?.ai_insights?.map((insight, i) => {
                  const colorMap = {
                    warning: 'border-amber-500/30 bg-amber-500/5',
                    info: 'border-blue-500/30 bg-blue-500/5',
                    opportunity: 'border-emerald-500/30 bg-emerald-500/5',
                    critical: 'border-red-500/30 bg-red-500/5',
                    success: 'border-purple-500/30 bg-purple-500/5',
                    action: 'border-cyan-500/30 bg-cyan-500/5'
                  };
                  const Icon = getInsightIcon(insight.type);
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className={`p-4 rounded-xl border ${colorMap[insight.type] || colorMap.info}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-slate-200">
                          <Icon size={18} className="text-slate-800" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-800">{insight.title}</h4>
                            {insight.count && (
                              <span className="px-2 py-0.5 rounded-full bg-slate-200 text-xs text-slate-700">
                                {insight.count.toLocaleString()}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{insight.text}</p>
                          {insight.category && (
                            <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs bg-slate-200 text-slate-500">
                              {insight.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart size={18} className="text-cyan-400" />
                  Arama Durumu (DataDurumAdi)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[350px]">
                  <div className="space-y-2">
                    {analytics?.status_distribution?.map((item, i) => {
                      const percentage = ((item.count / analytics.summary.total_calls) * 100).toFixed(1);
                      return (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100">
                          {getStatusIcon(item.status)}
                          <span className="flex-1 text-sm text-slate-700 truncate">{item.status}</span>
                          <span className="font-mono text-sm text-slate-500">{item.count.toLocaleString()}</span>
                          <span className="font-mono text-xs text-cyan-400 w-12 text-right">%{percentage}</span>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Last Status Distribution */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity size={18} className="text-amber-400" />
                  Son Durum (SonDataDurumAdi)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[350px]">
                  <div className="space-y-2">
                    {analytics?.last_status_distribution?.length > 0 ? (
                      analytics.last_status_distribution.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100">
                          <span className="flex-1 text-sm text-slate-700 truncate">{item.status}</span>
                          <span className="font-mono text-sm text-amber-400">{item.count.toLocaleString()}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-8">Henüz veri yok</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* NOTES TAB */}
      {activeTab === 'notes' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-rose-400">{analytics?.note_analysis?.fiyat_itirazi || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Fiyat İtirazı</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-400">{analytics?.note_analysis?.dusunecek_takip || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Düşünecek (Takip)</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-400">{analytics?.note_analysis?.mesaj_gonderilen || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Mesaj Gönderilen</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-400">{analytics?.note_analysis?.tekrar_aranacak || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Tekrar Aranacak</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Keywords */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText size={18} className="text-purple-400" />
                  En Çok Geçen Kelimeler
                </CardTitle>
                <CardDescription>Notlardaki anahtar kelimeler</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {analytics?.note_analysis?.top_keywords?.map((kw, i) => (
                    <span 
                      key={i} 
                      className="px-3 py-1.5 rounded-full text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                    >
                      {kw.word} <span className="text-purple-400">({kw.count})</span>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Sample Follow-ups */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock size={18} className="text-cyan-400" />
                  Takip Gerektiren Notlar
                </CardTitle>
                <CardDescription>Potansiyel müşteri notları</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px]">
                  <div className="space-y-3">
                    {analytics?.note_analysis?.sample_follow_ups?.map((item, i) => (
                      <div key={i} className="p-3 rounded-lg bg-slate-100">
                        <p className="text-sm text-slate-700">{item.note}</p>
                        <div className="flex gap-2 mt-2">
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">{item.status}</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-slate-200 text-slate-600">{item.agent}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* More note stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{analytics?.note_analysis?.ilgileniyor || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">İlgileniyor</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{analytics?.note_analysis?.vazgecti || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Vazgeçti</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-rose-400">{analytics?.note_analysis?.engelledi || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Engelledi</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{analytics?.note_analysis?.musait_degil || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Müsait Değil</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* DEMOGRAPHICS TAB */}
      {activeTab === 'demographics' && (
        <>
          {/* Demographics Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-900/10 border-blue-500/20">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-400">{analytics?.demographics?.ogrenci || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Öğrenci</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-900/10 border-emerald-500/20">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-400">{analytics?.demographics?.calisan || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Çalışan</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-900/10 border-amber-500/20">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-400">{analytics?.demographics?.issiz || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">İş Arıyor</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-900/10 border-purple-500/20">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-400">{analytics?.demographics?.emekli || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Emekli</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Education Distribution */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users size={18} className="text-blue-400" />
                  Eğitim Durumu (EgitimDurumuText)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {analytics?.education_distribution?.length > 0 ? (
                      analytics.education_distribution.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100">
                          <span className="flex-1 text-sm text-slate-700 truncate">{item.education}</span>
                          <span className="font-mono text-sm text-blue-400">{item.count.toLocaleString()}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-8">Henüz veri yok</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Course Distribution */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target size={18} className="text-emerald-400" />
                  Ders/Kurs Dağılımı (DersAdi)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {analytics?.course_distribution?.length > 0 ? (
                      analytics.course_distribution.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100">
                          <span className="flex-1 text-sm text-slate-700 truncate">{item.course}</span>
                          <span className="font-mono text-sm text-emerald-400">{item.count.toLocaleString()}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-8">Henüz veri yok</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* ADS TAB */}
      {activeTab === 'ads' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ad Type Distribution */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target size={18} className="text-amber-400" />
                Reklam Türü (ReklamTuruAdi)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[350px]">
                <div className="space-y-2">
                  {analytics?.ad_type_distribution?.length > 0 ? (
                    analytics.ad_type_distribution.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-100">
                        <div className="flex-1">
                          <span className="text-sm text-slate-700">{item.ad_type}</span>
                        </div>
                        <span className="font-mono text-amber-400">{item.count.toLocaleString()}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-8">Henüz veri yok - Excel'de ReklamTuruAdi sütunu olmalı</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Ad Text Distribution */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText size={18} className="text-purple-400" />
                Reklam Detayı (ReklamTuruText)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[350px]">
                <div className="space-y-2">
                  {analytics?.ad_text_distribution?.length > 0 ? (
                    analytics.ad_text_distribution.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-100">
                        <div className="flex-1">
                          <span className="text-sm text-slate-700">{item.ad_text}</span>
                        </div>
                        <span className="font-mono text-purple-400">{item.count.toLocaleString()}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-8">Henüz veri yok - Excel'de ReklamTuruText sütunu olmalı</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Data Description Distribution */}
          <Card className="bg-white border-slate-200 lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet size={18} className="text-cyan-400" />
                Data Açıklaması (DataAciklama)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px]">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {analytics?.data_description_distribution?.length > 0 ? (
                    analytics.data_description_distribution.map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-100">
                        <span className="text-sm text-slate-700 truncate flex-1">{item.description}</span>
                        <span className="font-mono text-sm text-cyan-400 ml-2">{item.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-8 col-span-3">Henüz veri yok - Excel'de DataAciklama sütunu olmalı</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AGENTS TAB */}
      {activeTab === 'agents' && (
        <>
          <Card className="bg-white border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity size={18} className="text-amber-400" />
                Danışman Performansı (DanismanAdSoyad)
              </CardTitle>
              <CardDescription>Detay için danışmana tıklayın</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {analytics?.agent_performance?.map((agent, i) => (
                    <motion.button
                      key={i}
                      whileHover={{ scale: 1.01 }}
                      onClick={() => fetchAgentAnalytics(agent._id)}
                      className={`p-4 rounded-lg text-left transition-colors ${
                        selectedAgent === agent._id ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-slate-100 hover:bg-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-800">{agent._id}</span>
                        <span className="font-mono text-emerald-400 font-bold">{agent.total_calls}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div className="text-center p-1 rounded bg-slate-50">
                          <p className="text-rose-400 font-mono">{agent.cevap_yok}</p>
                          <p className="text-slate-500">CY</p>
                        </div>
                        <div className="text-center p-1 rounded bg-slate-50">
                          <p className="text-amber-400 font-mono">{agent.mesgul}</p>
                          <p className="text-slate-500">M</p>
                        </div>
                        <div className="text-center p-1 rounded bg-slate-50">
                          <p className="text-blue-400 font-mono">{agent.dusunecek}</p>
                          <p className="text-slate-500">D</p>
                        </div>
                        <div className="text-center p-1 rounded bg-slate-50">
                          <p className="text-red-400 font-mono">{agent.istemiyor}</p>
                          <p className="text-slate-500">İ</p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Agent Detail */}
          {agentAnalytics && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{agentAnalytics.agent_name} - Detaylı Analiz</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => { setSelectedAgent(null); setAgentAnalytics(null); }}>
                    <X size={18} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="p-4 rounded-xl bg-slate-100">
                    <p className="text-sm text-slate-500">Toplam Satış</p>
                    <p className="text-2xl font-bold text-emerald-400">{agentAnalytics.sales?.total_sales || 0}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-100">
                    <p className="text-sm text-slate-500">Toplam Ciro</p>
                    <p className="text-2xl font-bold text-amber-400">₺{(agentAnalytics.sales?.total_revenue || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-100">
                    <p className="text-sm text-slate-500">Tahsil Edilen</p>
                    <p className="text-2xl font-bold text-purple-400">₺{(agentAnalytics.sales?.total_collected || 0).toLocaleString()}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-2">Durum Dağılımı:</p>
                  <div className="flex flex-wrap gap-2">
                    {agentAnalytics.status_distribution?.slice(0, 10).map((s, i) => (
                      <span key={i} className="px-3 py-1 rounded-full text-xs bg-slate-200 text-slate-700">
                        {s.status}: <span className="text-cyan-400">{s.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* CHARTS TAB */}
      {activeTab === 'charts' && <ChartsTab dateFrom={getDateRange().from} dateTo={getDateRange().to} />}

      {/* FUNNEL TAB */}
      {activeTab === 'funnel' && <FunnelTab dateFrom={getDateRange().from} dateTo={getDateRange().to} />}

      {/* COMPARISON TAB */}
      {activeTab === 'comparison' && <AgentComparisonTab dateFrom={getDateRange().from} dateTo={getDateRange().to} />}

      {/* PREDICTIONS TAB */}
      {activeTab === 'predictions' && <PredictionsTab dateFrom={getDateRange().from} dateTo={getDateRange().to} />}

      {/* ALERTS TAB */}
      {activeTab === 'alerts' && <AlertsTab dateFrom={getDateRange().from} dateTo={getDateRange().to} />}

      {/* EXPORT TAB */}
      {activeTab === 'export' && <ExportTab dateFrom={getDateRange().from} dateTo={getDateRange().to} />}
    </div>
  );
};

// Test Sale Section - Dashboard'da kutlamayı test etmek için
const TestSaleSection = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [amount, setAmount] = useState(25000);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    authAxios.get(`${API}/agents`).then(res => setAgents(res.data)).catch(console.error);
  }, []);

  const sendTestSale = async () => {
    if (!selectedAgent) {
      toast.error("Lütfen bir danışman seçin");
      return;
    }
    
    setLoading(true);
    try {
      await authAxios.post(`${API}/test-sale`, {
        agent_name: selectedAgent,
        amount: amount
      });
      toast.success(`Test satış gönderildi! Dashboard'u kontrol edin 🎉`);
    } catch (error) {
      toast.error("Satış gönderilemedi");
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Zap}
        title="Test Satış Gönder"
        description="Dashboard'daki geri sayım ve kutlama efektini test edin"
        color="bg-yellow-500/20 text-yellow-400"
      />

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap size={20} className="text-yellow-400" />
            Kutlama Testi
          </CardTitle>
          <CardDescription>
            Bu butona bastığınızda Dashboard'da geri sayım başlayacak ve kutlama efekti gösterilecek.
            <br />
            <span className="text-amber-400 font-medium">Not: Dashboard sayfasını ayrı bir sekmede açık tutun!</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Danışman Seçin</label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="w-full p-3 rounded-lg bg-slate-200 border border-slate-300 text-slate-800"
              >
                <option value="">-- Danışman Seçin --</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.name}>{agent.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Satış Tutarı (₺)</label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="bg-white border-slate-300"
              />
            </div>
          </div>

          <Button 
            onClick={sendTestSale} 
            disabled={loading || !selectedAgent}
            className="w-full h-14 text-lg bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
          >
            {loading ? (
              "Gönderiliyor..."
            ) : (
              <>
                <Zap size={24} className="mr-2" />
                TEST SATIŞ GÖNDER
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle>Nasıl Çalışır?</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-slate-500">
            <li>Dashboard sayfasını yeni bir sekmede açın</li>
            <li>Bu sayfada danışman seçin ve tutar girin</li>
            <li>"TEST SATIŞ GÖNDER" butonuna basın</li>
            <li>Dashboard'da 10'dan geriye doğru sayım başlayacak</li>
            <li>Sayım bitince konfeti + danışman ismi gösterilecek</li>
            <li>5 saniye sonra otomatik kapanacak</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

const GoalsSection = () => {
  const [goal, setGoal] = useState({ 
    sales_target: 10, 
    calls_target: 200, 
    revenue_target: 50000,
    monthly_sales_target: 300,
    monthly_calls_target: 6000,
    monthly_revenue_target: 1500000
  });
  const [currentGoal, setCurrentGoal] = useState(null);
  const [stats, setStats] = useState(null);
  const [perAgentCallTarget, setPerAgentCallTarget] = useState(50);
  const [commissionRate, setCommissionRate] = useState(5);

  useEffect(() => {
    // Fetch current goals
    authAxios.get(`${API}/goals/today`).then(res => {
      setGoal({
        sales_target: res.data.sales_target || 10,
        calls_target: res.data.calls_target || 200,
        revenue_target: res.data.revenue_target || 50000,
        monthly_sales_target: res.data.monthly_sales_target || 300,
        monthly_calls_target: res.data.monthly_calls_target || 6000,
        monthly_revenue_target: res.data.monthly_revenue_target || 1500000
      });
      setCurrentGoal(res.data);
    }).catch(console.error);
    
    // Fetch current stats
    authAxios.get(`${API}/stats`).then(res => {
      setStats(res.data);
    }).catch(console.error);

    // Fetch call target setting
    authAxios.get(`${API}/daily-call-targets`).then(res => {
      setPerAgentCallTarget(res.data.daily_call_target || 50);
    }).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await authAxios.put(`${API}/goals/today`, goal);
      await authAxios.put(`${API}/daily-call-target`, { daily_call_target: perAgentCallTarget });
      toast.success("Hedefler güncellendi!");
      const res = await authAxios.get(`${API}/goals/today`);
      setCurrentGoal(res.data);
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const dailyProgress = currentGoal ? Math.min(100, Math.round(((currentGoal.current_sales || 0) / (currentGoal.sales_target || 1)) * 100)) : 0;
  const monthlyProgress = stats && goal.monthly_sales_target ? Math.min(100, Math.round(((stats.total_sales || 0) / goal.monthly_sales_target) * 100)) : 0;

  return (
    <div data-testid="goals-section">
      <PageHeader 
        icon={Target} 
        title="Hedef Yönetimi" 
        description="Günlük ve aylık hedefleri belirleyin - Hedef tutunca kutlama yapılır!"
        color="bg-purple-500/10 text-purple-400"
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Goals */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target size={20} className="text-emerald-400" />
              Günlük Hedefler
            </CardTitle>
            <CardDescription>Her gün için hedefler (hedef tutunca havai fişek patlar!)</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-emerald-400" />
                  Günlük Satış Hedefi
                </Label>
                <Input
                  type="number"
                  value={goal.sales_target}
                  onChange={(e) => setGoal({ ...goal, sales_target: parseInt(e.target.value) || 0 })}
                  className="bg-white border-slate-300"
                  data-testid="sales-target-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone size={14} className="text-blue-400" />
                  Günlük Arama Hedefi
                </Label>
                <Input
                  type="number"
                  value={goal.calls_target}
                  onChange={(e) => setGoal({ ...goal, calls_target: parseInt(e.target.value) || 0 })}
                  className="bg-white border-slate-300"
                  data-testid="calls-target-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <DollarSign size={14} className="text-amber-400" />
                  Günlük Ciro Hedefi (₺)
                </Label>
                <Input
                  type="number"
                  value={goal.revenue_target}
                  onChange={(e) => setGoal({ ...goal, revenue_target: parseFloat(e.target.value) || 0 })}
                  className="bg-white border-slate-300"
                  data-testid="revenue-target-input"
                />
              </div>

              {/* Current Daily Progress */}
              {currentGoal && (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Bugünkü İlerleme</span>
                    <span className={`text-xl font-bold ${dailyProgress >= 100 ? 'text-emerald-400' : 'text-slate-800'}`}>
                      %{dailyProgress} {dailyProgress >= 100 && '🎉'}
                    </span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${dailyProgress}%` }}
                      className={`h-full ${dailyProgress >= 100 ? 'bg-emerald-400' : 'bg-emerald-600'}`}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {currentGoal.current_sales || 0} / {currentGoal.sales_target} satış
                  </p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Monthly Goals */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy size={20} className="text-amber-400" />
              Aylık Hedefler
            </CardTitle>
            <CardDescription>Bu ay için toplam hedefler</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-amber-400" />
                  Aylık Satış Hedefi
                </Label>
                <Input
                  type="number"
                  value={goal.monthly_sales_target}
                  onChange={(e) => setGoal({ ...goal, monthly_sales_target: parseInt(e.target.value) || 0 })}
                  className="bg-white border-slate-300"
                  data-testid="monthly-sales-target-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone size={14} className="text-blue-400" />
                  Aylık Arama Hedefi
                </Label>
                <Input
                  type="number"
                  value={goal.monthly_calls_target}
                  onChange={(e) => setGoal({ ...goal, monthly_calls_target: parseInt(e.target.value) || 0 })}
                  className="bg-white border-slate-300"
                  data-testid="monthly-calls-target-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <DollarSign size={14} className="text-amber-400" />
                  Aylık Ciro Hedefi (₺)
                </Label>
                <Input
                  type="number"
                  value={goal.monthly_revenue_target}
                  onChange={(e) => setGoal({ ...goal, monthly_revenue_target: parseFloat(e.target.value) || 0 })}
                  className="bg-white border-slate-300"
                  data-testid="monthly-revenue-target-input"
                />
              </div>

              {/* Current Monthly Progress */}
              {stats && (
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Bu Ay İlerleme</span>
                    <span className={`text-xl font-bold ${monthlyProgress >= 100 ? 'text-amber-400' : 'text-slate-800'}`}>
                      %{monthlyProgress} {monthlyProgress >= 100 && '🏆'}
                    </span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${monthlyProgress}%` }}
                      className={`h-full ${monthlyProgress >= 100 ? 'bg-amber-400' : 'bg-amber-600'}`}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {stats.total_sales || 0} / {goal.monthly_sales_target} satış | ₺{(stats.total_revenue || 0).toLocaleString()} / ₺{goal.monthly_revenue_target.toLocaleString()}
                  </p>
                </div>
              )}

              <Button type="submit" onClick={handleSubmit} className="w-full bg-purple-600 hover:bg-purple-500 h-12" data-testid="save-goals-btn">
                <Target size={18} className="mr-2" />
                Tüm Hedefleri Kaydet
              </Button>

              {/* Kişi Başı Ayarlar */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <h3 className="font-heading text-base font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <Users size={16} className="text-violet-500" /> Dashboard Motivasyon Ayarları
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Kişi Başı Günlük Arama Hedefi</label>
                    <Input
                      type="number"
                      className="bg-white border-slate-300"
                      value={perAgentCallTarget}
                      onChange={e => setPerAgentCallTarget(Number(e.target.value))}
                      data-testid="per-agent-call-target"
                    />
                    <p className="text-xs text-slate-400 mt-1">Dashboard'da her danışmanın ilerleme çubuğunda görünür</p>
                  </div>
                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Komisyon Oranı (%)</label>
                    <Input
                      type="number"
                      className="bg-white border-slate-300"
                      value={commissionRate}
                      onChange={e => setCommissionRate(Number(e.target.value))}
                      data-testid="commission-rate"
                    />
                    <p className="text-xs text-slate-400 mt-1">Tahmini komisyon hesaplamasında kullanılır</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-white border-slate-200 mt-6">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-purple-500/20">
              <Zap size={24} className="text-purple-400" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">Kutlama Sistemi</h4>
              <p className="text-sm text-slate-500">
                Günlük veya aylık hedef tuttuğunda ana ekranda otomatik olarak havai fişek patlar ve kutlama müziği çalar! 
                Ekibinizi motive etmek için hedefleri belirleyin.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Slogans Section - Motivasyon Sloganları Yönetimi
const SlogansSection = () => {
  const [slogans, setSlogans] = useState([]);
  const [newSlogan, setNewSlogan] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchSlogans = async () => {
    try {
      const res = await authAxios.get(`${API}/slogans`);
      setSlogans(res.data || []);
    } catch (error) {
      console.error("Slogans fetch error:", error);
    }
  };

  useEffect(() => {
    fetchSlogans();
  }, []);

  const addSlogan = async () => {
    if (!newSlogan.trim()) return;
    setLoading(true);
    try {
      await authAxios.post(`${API}/slogans`, { text: newSlogan.trim() });
      toast.success("Slogan eklendi!");
      setNewSlogan("");
      fetchSlogans();
    } catch (error) {
      toast.error("Ekleme hatası!");
    }
    setLoading(false);
  };

  const deleteSlogan = async (sloganId) => {
    setLoading(true);
    try {
      await authAxios.delete(`${API}/slogans/${sloganId}`);
      toast.success("Slogan silindi!");
      fetchSlogans();
    } catch (error) {
      toast.error("Silme hatası!");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Flame}
        title="Motivasyon Sloganları"
        description="Dashboard'da kayan sloganları yönetin"
        color="bg-orange-500/20 text-orange-400"
      />

      {/* Add New Slogan */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus size={20} className="text-orange-400" />
            Yeni Slogan Ekle
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder="Motivasyon sloganı yazın..."
              value={newSlogan}
              onChange={(e) => setNewSlogan(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addSlogan()}
              className="bg-white border-slate-300"
            />
            <Button onClick={addSlogan} disabled={loading || !newSlogan.trim()} className="bg-orange-500 hover:bg-orange-600">
              <Plus size={18} className="mr-2" /> Ekle
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Slogans List */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle>Mevcut Sloganlar ({slogans.length})</CardTitle>
          <CardDescription>Sloganlar dashboard'da sırayla gösterilecek</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {slogans.map((slogan, index) => (
              <motion.div
                key={slogan.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-100 border border-slate-200 group"
              >
                <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold">
                  {index + 1}
                </div>
                <span className="flex-1 text-slate-800">{slogan.text}</span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => deleteSlogan(slogan.id)} 
                  className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={loading}
                >
                  <Trash2 size={14} />
                </Button>
              </motion.div>
            ))}
            {slogans.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                Henüz slogan eklenmemiş
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle>Önizleme</CardTitle>
          <CardDescription>Sloganlar dashboard'da böyle görünecek</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-rose-500/10 to-rose-900/10 border border-rose-500/20">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-lg bg-rose-400/10">
                <Flame size={20} className="text-rose-400" />
              </div>
              <span className="text-slate-500 text-sm">Motivasyon</span>
            </div>
            <AnimatePresence mode="wait">
              {slogans.length > 0 && (
                <motion.div
                  key={slogans[0]?.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-heading font-bold text-2xl text-rose-400"
                >
                  {slogans[0]?.text}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Badges Section
const BadgesSection = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const fetchAgents = async () => {
    try {
      const res = await authAxios.get(`${API}/agents`);
      setAgents(res.data);
      if (selectedAgent) {
        const updated = res.data.find(a => a.id === selectedAgent.id);
        if (updated) setSelectedAgent(updated);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => { fetchAgents(); }, []);

  const handleAddBadge = async (agentId, badge) => {
    try {
      await authAxios.post(`${API}/agents/${agentId}/badges/${badge}`);
      toast.success("Rozet eklendi! 🏆");
      fetchAgents();
    } catch (error) {
      toast.error("Rozet eklenemedi");
    }
  };

  const handleRemoveBadge = async (agentId, badge) => {
    try {
      await authAxios.delete(`${API}/agents/${agentId}/badges/${badge}`);
      toast.success("Rozet kaldırıldı");
      fetchAgents();
    } catch (error) {
      toast.error("Rozet kaldırılamadı");
    }
  };

  return (
    <div data-testid="badges-section">
      <PageHeader 
        icon={Award} 
        title="Rozet Yönetimi" 
        description="Çalışanlara rozet verin"
        color="bg-rose-500/10 text-rose-400"
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle>Çalışan Seçin</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {agents.map((agent) => (
                  <motion.button
                    key={agent.id}
                    whileHover={{ scale: 1.01 }}
                    className={`w-full p-4 rounded-xl flex items-center gap-4 transition-colors ${
                      selectedAgent?.id === agent.id
                        ? "bg-rose-500/10 border-2 border-rose-500/50"
                        : "bg-slate-100 hover:bg-slate-200 border-2 border-transparent"
                    }`}
                    onClick={() => setSelectedAgent(agent)}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg"
                      style={{ backgroundColor: agent.avatar_color }}
                    >
                      {agent.name.charAt(0)}
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold text-slate-800">{agent.name}</div>
                      <div className="flex gap-1 mt-1">
                        {agent.badges.slice(0, 4).map((badge, i) => {
                          const b = BADGE_OPTIONS.find(opt => opt.value === badge);
                          const Icon = b?.icon || Award;
                          return <Icon key={i} size={14} className={b?.color.split(' ')[0] || 'text-slate-500'} />;
                        })}
                        {agent.badges.length === 0 && <span className="text-slate-400 text-sm">Rozet yok</span>}
                      </div>
                    </div>
                    {selectedAgent?.id === agent.id && <ChevronRight className="text-rose-400" />}
                  </motion.button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle>{selectedAgent ? `${selectedAgent.name} - Rozetler` : "Çalışan Seçin"}</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedAgent ? (
              <div className="space-y-6">
                <div>
                  <Label className="mb-3 block">Mevcut Rozetler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedAgent.badges.length > 0 ? (
                      selectedAgent.badges.map((badge) => {
                        const b = BADGE_OPTIONS.find(opt => opt.value === badge);
                        const Icon = b?.icon || Award;
                        return (
                          <motion.button
                            key={badge}
                            whileHover={{ scale: 1.05 }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${b?.color || 'bg-slate-200 text-slate-500'}`}
                            onClick={() => handleRemoveBadge(selectedAgent.id, badge)}
                          >
                            <Icon size={16} />
                            <span className="font-medium">{b?.label || badge}</span>
                            <X size={14} className="text-red-400 ml-1" />
                          </motion.button>
                        );
                      })
                    ) : (
                      <span className="text-slate-500">Henüz rozet yok</span>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="mb-3 block">Rozet Ekle</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {BADGE_OPTIONS.filter(b => !selectedAgent.badges.includes(b.value)).map((badge) => (
                      <Button
                        key={badge.value}
                        variant="outline"
                        className={`justify-start gap-3 h-12 border-slate-300 hover:border-blue-400 ${badge.color}`}
                        onClick={() => handleAddBadge(selectedAgent.id, badge.value)}
                      >
                        <badge.icon size={18} />
                        {badge.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Award size={48} className="mx-auto mb-4 opacity-50" />
                <p>Sol taraftan bir çalışan seçin</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Music Section
const MusicSection = () => {
  const [settings, setSettings] = useState({ celebration_music_url: "", celebration_sound_enabled: true });
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploadedSounds, setUploadedSounds] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const audioRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  const PRESET_SOUNDS = [
    { name: "Zafer Fanfari", url: "https://assets.mixkit.co/active_storage/sfx/2020/2020-preview.mp3", emoji: "🏆" },
    { name: "Kutlama Borusu", url: "https://assets.mixkit.co/active_storage/sfx/2013/2013-preview.mp3", emoji: "🎺" },
    { name: "Başarı Melodisi", url: "https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3", emoji: "✨" },
    { name: "Alkış Sesi", url: "https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3", emoji: "👏" },
    { name: "Parti Düdüğü", url: "https://assets.mixkit.co/active_storage/sfx/2572/2572-preview.mp3", emoji: "🎉" },
  ];

  const fetchData = async () => {
    try {
      const [settingsRes, soundsRes] = await Promise.all([
        authAxios.get(`${API}/settings`),
        authAxios.get(`${API}/settings/sounds`)
      ]);
      setSettings(settingsRes.data);
      setUploadedSounds(soundsRes.data.sounds || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    try {
      await authAxios.put(`${API}/settings`, settings);
      toast.success("Ayarlar kaydedildi! 🎵");
    } catch (error) {
      toast.error("Kayıt başarısız");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/x-m4a'];
    if (!validTypes.includes(file.type)) {
      toast.error("Sadece MP3, WAV, OGG veya M4A dosyaları yüklenebilir");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Dosya boyutu 5MB'dan küçük olmalı");
      return;
    }

    setIsUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        const res = await authAxios.post(`${API}/settings/upload-sound`, {
          filename: file.name,
          data: base64
        });

        toast.success("Ses dosyası yüklendi! 🎵");
        setSettings({ ...settings, celebration_music_url: res.data.url });
        fetchData();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Yükleme başarısız");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteSound = async (filename) => {
    try {
      await authAxios.delete(`${API}/settings/sounds/${filename}`);
      toast.success("Ses silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const previewSound = (url) => {
    if (audioRef.current) audioRef.current.pause();
    // Handle relative URLs
    const fullUrl = url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
    audioRef.current = new Audio(fullUrl);
    audioRef.current.volume = 0.5;
    audioRef.current.play();
    setIsPlaying(true);
    audioRef.current.onended = () => setIsPlaying(false);
  };

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  return (
    <div data-testid="music-section">
      <PageHeader 
        icon={Music} 
        title="Kutlama Müziği" 
        description="Satış kutlaması için ses ayarları"
        color="bg-cyan-500/10 text-cyan-400"
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Volume2 size={18} className="text-cyan-400" />
              Ses Ayarları
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-100">
              <div>
                <Label>Kutlama Sesi</Label>
                <p className="text-sm text-slate-500">Yeni satışlarda ses çal</p>
              </div>
              <Switch
                checked={settings.celebration_sound_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, celebration_sound_enabled: checked })}
                data-testid="sound-enabled-switch"
              />
            </div>

            <div className="space-y-2">
              <Label>Müzik URL</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://..."
                  value={settings.celebration_music_url}
                  onChange={(e) => setSettings({ ...settings, celebration_music_url: e.target.value })}
                  className="bg-white border-slate-300 text-sm"
                  data-testid="music-url-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => previewSound(settings.celebration_music_url)}
                  disabled={!settings.celebration_music_url}
                  className="border-slate-300"
                >
                  <Play size={16} />
                </Button>
              </div>
            </div>

            <Button onClick={handleSave} className="w-full bg-cyan-600 hover:bg-cyan-500" data-testid="save-music-btn">
              <Save size={16} className="mr-2" />
              Kaydet
            </Button>
          </CardContent>
        </Card>

        {/* Upload Section */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus size={18} className="text-emerald-400" />
              Ses Yükle
            </CardTitle>
            <CardDescription>Kendi ses dosyanızı yükleyin (MP3, WAV, OGG - Max 5MB)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.ogg,.m4a,audio/*"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="file-input"
            />
            
            <Button
              variant="outline"
              className="w-full h-24 border-dashed border-2 border-slate-300 hover:border-emerald-500/50 flex flex-col gap-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                    <Music size={24} className="text-emerald-400" />
                  </motion.div>
                  <span className="text-sm text-slate-500">Yükleniyor...</span>
                </>
              ) : (
                <>
                  <Plus size={24} className="text-slate-500" />
                  <span className="text-sm text-slate-500">Ses Dosyası Seç</span>
                </>
              )}
            </Button>

            {/* Uploaded Sounds */}
            {uploadedSounds.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-500">Yüklenen Sesler</Label>
                {uploadedSounds.map((sound, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg flex items-center gap-3 transition-all cursor-pointer ${
                      settings.celebration_music_url === sound.url
                        ? "bg-emerald-500/20 border border-emerald-500/50"
                        : "bg-slate-100 hover:bg-slate-200"
                    }`}
                    onClick={() => {
                      setSettings({ ...settings, celebration_music_url: sound.url });
                      previewSound(sound.url);
                    }}
                  >
                    <Music size={16} className="text-emerald-400" />
                    <span className="flex-1 text-sm truncate">{sound.filename}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        previewSound(sound.url);
                      }}
                    >
                      <Play size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 hover:bg-red-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSound(sound.filename);
                      }}
                    >
                      <Trash2 size={14} className="text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preset Sounds */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music size={18} className="text-amber-400" />
              Hazır Sesler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {PRESET_SOUNDS.map((sound, i) => (
                <motion.button
                  key={i}
                  whileHover={{ scale: 1.01 }}
                  className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                    settings.celebration_music_url === sound.url
                      ? "bg-amber-500/10 border border-amber-500/50"
                      : "bg-slate-100 hover:bg-slate-200"
                  }`}
                  onClick={() => {
                    setSettings({ ...settings, celebration_music_url: sound.url });
                    previewSound(sound.url);
                  }}
                >
                  <span className="text-xl">{sound.emoji}</span>
                  <span className="font-medium flex-1 text-left text-sm">{sound.name}</span>
                  <Play size={16} className="text-slate-500" />
                </motion.button>
              ))}
            </div>

            {isPlaying && (
              <Button variant="outline" className="w-full mt-4 border-slate-300" onClick={stopPreview}>
                <X size={16} className="mr-2" />
                Durdur
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="bg-white border-slate-200 mt-6">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-purple-500/10">
              <Zap size={24} className="text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 mb-1">15 Saniye Kutlama</h3>
              <p className="text-sm text-slate-500">
                Her yeni satış kaydedildiğinde 15 saniyelik havai fişek gösterisi ve seçtiğiniz müzik çalacaktır.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Admin
export default function Admin() {
  const { section = "agent-reports" } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!["crm-tracker", "data-center", "agent-reports", "ai-analysis", "agent-users", "agents", "calls", "sales", "students", "kariyer-paneli", "excel-import", "goals", "slogans", "music", "test-sale"].includes(section)) {
      navigate("/admin/agent-reports");
    }
  }, [section, navigate]);

  const renderSection = () => {
    switch (section) {
      case "crm-tracker": return <CRMDataTracker token={localStorage.getItem("authToken")} />;
      case "data-center": return <DataCenter />;
      case "agent-reports": return <AgentReportsSection />;
      case "ai-analysis": return <AIAnalysis />;
      case "agent-users": return <AgentUsersSection />;
      case "agents": return <AgentsSection />;
      case "calls": return <CallsSection />;
      case "sales": return <SalesSection />;
      case "students": return <StudentsSection />;
      case "kariyer-paneli": return <KariyerPaneliSection />;
      case "excel-import": return <ExcelImportSection />;
      case "goals": return <GoalsSection />;
      case "slogans": return <SlogansSection />;
      case "music": return <MusicSection />;
      case "test-sale": return <TestSaleSection />;
      default: return <AgentReportsSection />;
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 admin-light-theme" data-testid="admin-panel">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg bg-slate-200 text-slate-700"
          >
            <Menu size={20} />
          </button>
          <h1 className="font-bold text-slate-800">Admin Panel</h1>
          <Link to="/" className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400">
            <Home size={20} />
          </Link>
        </div>
      </div>

      <Sidebar activeSection={section} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <main className="lg:ml-72 p-4 md:p-6 lg:p-8 pt-20 lg:pt-8">
        <motion.div
          key={section}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderSection()}
        </motion.div>
      </main>
    </div>
  );
}
